import {
  compassImageUrl,
  courtImageUrl,
  lightBulbImageUrl,
  lightningImageUrl,
  peopleImageImageUrl,
  shuffleImageUrl,
  TmiType,
} from '@app/app/page-modules/personality-indicator/pi-questions/models/pi-questions.model';

export const tmiTypesReportDe: {
  [trait: string]: TmiType;
} = {
  ENFJ: {
    shortDescription: 'Geber',
    atWorkParagraphs: {
      effectiveness: [
        'Stellt die Aufgabenfertigstellung stärker in den Vordergrund',
        'Ist neugieriger, skeptischer und kritischer',
        'Weniger loyal gegenüber Menschen oder Anliegen, die es vielleicht nicht verdienen',
        'Handelt häufiger spontan',
        'Ist weniger herrisch gegenüber Menschen, die ihnen wichtig sind, und lässt sie ihre eigenen Fehler machen und daraus lernen',
        'Verbringt weniger Zeit mit zwischenmenschlichen Themen',
        'Baut auf ihrer Stärke auf, um dem Team Struktur und Organisation zu geben',
        'Baut auf ihrer Stärke auf, um durch Klarheit, Zusammenarbeit und Kooperation Konsens zu erreichen',
        'Erkennt, dass Theorien nicht immer funktionieren',
        'Konzentriert sich auf das Positive statt auf das Negative',
        'Erkennt, dass Entscheidungen manchmal mit unvollständigen Informationen getroffen werden müssen',
      ],
      teamLeader: [
        'Begeistert und inspiriert das Team mit Enthusiasmus und Humor',
        'Hat einen egalitären Führungsstil',
        'Gleicht Prozess und Inhalt gleichermaßen aus',
        'Schafft eine klare Teamorganisation',
        'Sorgt dafür, dass das Team auf Kurs bleibt, indem es Agenden, Zeitpläne und Aufgaben erledigt',
        'Hört allen Teammitgliedern zu und berücksichtigt jede Meinung',
        'Erleichtert die Zusammenarbeit und nutzt alle verfügbaren Ressourcen effektiv',
        'Führt das Team mit Einsicht und Sensibilität',
      ],
      conflicts: [
        'Wird wahrscheinlich von Teammitgliedern irritiert, die unvorbereitet zu Meetings erscheinen',
        'Kann Konflikte mit Menschen haben, die sie als unengagiert bei der Lösung von Teamproblemen wahrnehmen',
        'Hat weniger gute Beziehungen zu Menschen, die die Zeit anderer durch Zuspätkommen oder frühes Gehen missachten',
        'Kann eher Konflikte mit Menschen haben, die sich nicht genügend um Zusammenarbeit bemühen',
        'Hat möglicherweise eine geringe Toleranz gegenüber Menschen, die die Auswirkungen ihrer Entscheidungen auf andere nicht berücksichtigen',
        'Kann sich eher von Menschen gestört fühlen, die nicht genug Zeit für zwischenmenschliche Höflichkeiten lassen',
      ],
      stressAdvisor: [
        'Wird zunehmend kritisch und findet an allem Fehler',
        'Fixiert sich darauf, von allen gemocht zu werden und ihre Zustimmung zu gewinnen',
        'Wird herrisch und möchte alles organisieren',
      ],
      workEnvironment: [
        'Bevorzugt eine beschäftigte, interaktive Umgebung und strebt nach Harmonie',
        'Bevorzugt einen Plan, eine Routine und hält sich daran',
      ],
      underStress: [],
      underSevereStress: [],
    },
    atWorkTagLine: 'Begeistert und inspiriert das Team mit Enthusiasmus und Humor',
    atWorkSneakPeek: 'Eine Möglichkeit, die Effektivität zu steigern, besteht darin, neugieriger, skeptischer und kritischer zu sein',
    withOthersTagLine: 'Aufgeschlossen, freundlich und gesellig',
    withOthersSneakPeek:
      '%fname fühlt sich möglicherweise am wohlsten in den folgenden Rollen im Team: Der Organisator, Der Ideenlieferant, Der Moderator',
    withOthersParagraphs: {
      strengths: [
        'Aufgeschlossen, freundlich und gesellig',
        'Beliebt, mitfühlend',
        'Ausdrucksstark',
        'Gibt Ermutigung und Lob frei',
        'Genießt Smalltalk',
        'Individueller Stil, denkt aber immer an die Gruppe',
      ],
      weakSpotsWithOthers: [
        'Überempfindlich gegenüber Konflikten',
        'Braucht möglicherweise Ermutigung und Anerkennung',
        'Braucht möglicherweise Zeit für zwischenmenschliche Höflichkeiten',
        'Sieht Kritik, wo keine beabsichtigt war',
        'Handelt wahrscheinlich nicht spontan',
      ],
    },
    typeTagLine: 'Kann Muster und Bedeutungen hinter der Oberfläche erkennen',
    typeSneakPeek: '%fname-Typ wird ENFJ genannt. Einige zugehörige Stichworte sind mitfühlend, harmonisch und Katalysator.',
    typeKeywords: [
      '1. Kooperativ',
      '2. Mitfühlend',
      '3. Suche nach sich selbst',
      '4. Katalysator',
      '5. Beziehungsaufbau',
      '6. Möglichkeiten',
      '7. Mitfühlend',
      '8. Organisiert',
      '9. Detailorientiert',
      '10. Harmonie',
      '11. Sensibel',
    ],
    typePersonalStrengths: [
      'Kann Muster und Bedeutungen hinter der Oberfläche erkennen',
      'Gut im Fördern von Kommunikation',
      'Kreativ, mit starker Vorstellungskraft',
      'Verantwortungsbewusster Arbeitsansatz',
      'Gut im Präsentieren',
      'Gut im Führen von Diskussionen mit Leichtigkeit und Takt',
      'Auf Lob und Kritik reaktionsfähig',
      'Zuverlässig, vertrauenswürdig und loyal',
      'Gut im Beachten von Details und im Organisieren der Welt um sich herum',
      'Engagiert für das Gemeinwohl, natürlicher Kooperationspartner',
      'Warmherzig, sensibel, verständnisvoll und empathisch',
      'Kann Aufgaben erledigen',
      'Hoch sensibilisiert für die Emotionen, Bedürfnisse und Motivationen anderer',
      'Bestrebt, das Beste in jedem hervorzubringen',
      'Kann theoretische Konzepte geordnet und methodisch anwenden',
      'Gute Kenntnis theoretischer Konzepte und grundlegender Prinzipien',
      'Kann Probleme systematisch angehen',
    ],
    typeWeakSpots: [
      'Mag keine Streitigkeiten und Meinungsverschiedenheiten',
      'Findet es nicht einfach, andere zu beaufsichtigen',
      'Bevorzugt eine klar definierte Teamrolle',
      'Kann manchmal praktische Aspekte aus den Augen verlieren',
      'Kann sich leicht verletzt oder zurückgewiesen fühlen',
    ],
    description:
      'Der ENFJ ist eine enthusiastische, umgängliche und reaktionsfähige Person mit starkem Fokus auf Beziehungen und Interaktion. Auf der Suche nach Harmonie glauben sie stark an Menschen, manchmal bis zum Übermaß. Der ENFJ ist warmherzig und altruistisch, voller Charisma und kann Menschen dazu bringen, ihrem Beispiel zu folgen. Sie sind natürliche, leicht zu kommunizierende Personen, die für ihre Überzeugungen einstehen. Zuverlässig und organisiert haben sie den Wunsch, Ergebnisse zu liefern, ohne die Bedürfnisse anderer aus den Augen zu verlieren.',
    individualTypeByLetters: 'Geber',
    individualDescription:
      'Als Geber sind sie wahrscheinlich enthusiastische, umgängliche und reaktionsfähige Personen mit starkem Fokus auf Beziehungen und Interaktion. Auf der Suche nach Harmonie glauben sie stark an Menschen, manchmal bis zum Übermaß. Sie sind wahrscheinlich warmherzig und altruistisch, voller Charisma und können Menschen dazu bringen, ihrem Beispiel zu folgen. Sie sind wahrscheinlich natürliche, leicht kommunizierende Personen, die für ihre Überzeugungen einstehen. Zuverlässig und organisiert möchten sie Ergebnisse liefern, achten dabei jedoch darauf, die Bedürfnisse anderer nicht aus den Augen zu verlieren.',
  },
  ENFP: {
    shortDescription: 'Inspirator',
    atWorkParagraphs: {
      effectiveness: [
        'Entscheidungen nicht zu persönlich nehmen',
        'Bedenken, dass die Umsetzung ihrer Ideen komplexer ist und länger dauert, als sie die Ideen entwickeln',
        'Pragmatischer sein',
        'Eigene intuitive Einsichten häufiger hinterfragen',
        'Sicherstellen, dass keine wichtigen Fakten übersehen wurden',
        'Leise Teammitglieder genauer zuhören',
        'Durchsetzungsfähigkeit entwickeln',
        'Kritische Haltung gegenüber anderen Standpunkten einnehmen',
        'Genügend Zeit nehmen, um innezuhalten und zu reflektieren, bevor gehandelt wird',
        'Öfter „Nein“ sagen versuchen',
        'Kritik nicht persönlich nehmen',
        'Sicherstellen, dass nicht mehr versprochen wird, als vernünftigerweise möglich ist',
        'Darauf achten, dass das Team nicht durch übermäßiges Reden oder zufälliges Einbringen von Ideen vom Kurs abkommt',
      ],
      teamLeader: [
        'Andere durch Begeisterung inspirieren',
        'Sicherstellen, dass jede Stimme im Team gehört und verstanden wird. Sorgfältig zwischen unterschiedlichen Meinungen verhandeln',
        'Bereiche der Übereinstimmung hervorheben und Gesamtkonsens sicherstellen',
        'Persönliche Beziehungen zu jedem Teammitglied aufbauen',
        'Teammitglieder befähigen, ihre Beiträge zu teilen, anzuerkennen und zu validieren',
        'Ideen und Visionen aller Teammitglieder bei der Entwicklung von Strategien einholen',
        'Positive Alternativen für den Entscheidungsprozess präsentieren',
        'Entwicklungsmöglichkeiten für jedes Teammitglied suchen',
      ],
      conflicts: [
        'Konflikte mit Teammitgliedern möglich, die sie als zu engstirnig wahrnehmen',
        'Können sich von Menschen irritiert fühlen, die zu negativ oder ständig pessimistisch erscheinen',
        'Weniger gute Beziehungen zu Menschen, die zu stark auf Details fokussiert sind',
        'Können sich über Menschen ärgern, die nicht alle in den Teamprozess einbeziehen',
        'Wahrscheinlich irritiert über Menschen, die zu viel Zeit damit verbringen, über zu erledigende Aufgaben zu streiten',
      ],
      stressAdvisor: [
        'Können Schwierigkeiten haben, „Nein“ zu sagen',
        'Können zu viele Projekte übernehmen, ohne praktische Möglichkeit zur Umsetzung',
        'Unter starkem Stress fällt es schwer, sich von einem Projekt zurückzuziehen, um praktische oder körperliche Bedürfnisse zu erfüllen',
      ],
      workEnvironment: [
        'Eine interaktive, gesellige Umgebung',
        'Arbeiten an etwas Sinnvollem und Nutzung von Vorstellungskraft und Kreativität',
      ],
      underStress: [
        'Kann zunehmend Details vermeiden und die Fakten verwirren',
        'Neigt dazu, sich zurückzuziehen und negativ/pessimistisch zu werden',
        'Schwierigkeiten, sich auf Routineverfahren zu konzentrieren und diese durchzuführen',
      ],
      underSevereStress: [
        'Unter starkem Stress fällt es schwer, sich von einem Projekt zurückzuziehen, um praktische oder körperliche Bedürfnisse zu erfüllen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, Entscheidungen nicht zu persönlich zu nehmen',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Ideenlieferant, Der Moderator oder Der Grenzgänger',
    withOthersParagraphs: {
      strengths: [
        'Gesellig und aufgeschlossen',
        'Leicht beliebt und populär',
        'Zeigt großes Interesse an Menschen um sich herum',
        'Wird typischerweise als warmherzig, empathisch und fürsorglich wahrgenommen',
        'Bewertet andere Standpunkte mit Sensibilität und Takt',
        'Knüpft leicht Freundschaften',
        'Freidenkend und offen',
        'Harmonisch',
        'Entgegenkommend',
        'Schätzt Integrität hoch',
      ],
      weakSpotsWithOthers: [
        'Schwierigkeiten, andere direkt zu konfrontieren',
        'Kann andere zu eigenen Lasten berücksichtigen',
        'Kann verärgert werden, wenn sie das Gefühl haben, ausgenutzt zu werden',
        'Kann entmutigt werden, wenn ihre Ideen und Meinungen nicht geschätzt werden',
        'Andere könnten sie als etwas unorganisiert wahrnehmen',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ENFP genannt. Einige zugehörige Stichworte sind enthusiastisch, visionär und warmherzig.',
    typeKeywords: [
      '1. Enthusiastisch',
      '2. Energisch',
      '3. Unterstützend',
      '4. Gesellig',
      '5. Visionär',
      '6. Menschenorientiert',
      '7. Kreativ',
      '8. Fürsorglich',
      '9. Warmherzig',
      '10. Mitfühlend',
      '11. Spontan',
      '12. Flexibel',
    ],
    typePersonalStrengths: [
      'Begeistert und motiviert andere',
      'Sieht das Leben als voller Möglichkeiten',
      'Fördert Kommunikation zwischen Menschen',
      'Kreativ',
      'Entwickelt neue Lösungen für Probleme',
      'Wahrnehmung dessen, was anderen Menschen wichtig ist',
      'Improvisation',
      'Oft verbal begabt',
      'Kann das große Ganze sehen',
      'Kann schnell Zusammenhänge zwischen Ereignissen und Informationen erkennen',
      'Denkt in Metaphern',
      'Natürlicher Brainstormer',
      'Natürlich kooperativ und motiviert, für das Gemeinwohl zu arbeiten',
      'Starke soziale Fähigkeiten',
      'Anpassungsfähig an Veränderungen',
      'Gut im Erklären und Helfen anderer beim Verstehen',
      'Gut darin, Veränderungen durch Beziehungsaufbau zu fördern',
      'Idealistisch, positive Lebenseinstellung',
    ],
    typeWeakSpots: [
      'Überempfindlich gegenüber Konflikten',
      'Braucht viel Bestätigung von anderen',
      'Manchmal überinterpretiert und zieht zu schnell Verbindungen zwischen Ereignissen und Informationen',
      'Wünscht sich ständige Ermutigung und Anerkennung',
      'Begeistert am Anfang, kann aber Nachverfolgung fehlen',
      'Kann Entscheidungen verzögern, um Optionen offen zu halten',
      'Schwierigkeiten, sich im Voraus vorzubereiten',
      'Schwierigkeiten, auf Details zu achten',
      'Intolerant gegenüber Routine',
    ],
    description:
      'Der ENFP ist ein geselliger Mensch, dessen Begeisterung dazu führt, dass er häufig in Gesellschaft anderer ist. Sie werden oft als das Herz und die Seele jeder Gruppe beschrieben. Der ENFP ist neugierig und unabhängig, genießt Veränderung und Flexibilität, verliert jedoch leicht den Fokus. ENFPs haben viele „große Ideen“ und suchen nach Handlungsmöglichkeiten. Sie möchten alle in ihre Ideen und Pläne einbeziehen, um Kreativität und Innovation zu fördern. Sie kümmern sich tief um andere und deren Meinung und können das Leben der Menschen um sie herum bereichern.',
    individualTypeByLetters: 'Inspirator',
    individualDescription:
      'Als Inspirator sind sie wahrscheinlich gesellige Menschen, deren Begeisterung dazu führt, dass sie häufig in Gesellschaft anderer sind. Sie werden oft als das Herz und die Seele jeder Gruppe beschrieben. Neugierig und unabhängig genießen sie Veränderung und Flexibilität, verlieren jedoch leicht den Fokus. Sie haben viele „große Ideen“ und suchen nach Handlungsmöglichkeiten, wobei sie alle in ihre Ideen und Pläne einbeziehen möchten, um Kreativität und Innovation zu fördern. Sie kümmern sich wahrscheinlich sehr um andere und deren Meinung und können das Leben der Menschen um sie herum bereichern.',
  },
  ENTJ: {
    shortDescription: 'Führungskraft',
    atWorkParagraphs: {
      effectiveness: [
        'Geduldiger mit Menschen sein, die mehr Zeit benötigen, um komplexe, abstrakte Ideen zu verstehen',
        'Eine höhere Toleranz gegenüber wahrgenommener Inkompetenz oder Ineffizienz anderer entwickeln',
        'Weniger kritisch und akzeptierender gegenüber Stärken und Schwächen anderer sein',
        'Keine unerreichbaren Standards setzen',
        'Die emotionalen Bedürfnisse anderer anerkennen',
        'Konstruktive Wege zur Kritik finden',
        'Einen gesunden Respekt vor Details entwickeln',
        'Mehr zuhören und weniger reden versuchen',
        'Mehr Zeit für private Reflexion einplanen',
      ],
      teamLeader: [
        'Eine koordinierende Rolle übernehmen',
        'Das Verständnis und die Erledigung von Aufgaben durch Bereitstellung von Modellen verbessern',
        'Visionen so kommunizieren, dass andere zu aktiver Teilnahme motiviert werden',
        'Objektiver sein, um die Anstrengungen und Aktivitäten anderer auf ein übergeordnetes Ziel zu lenken',
        'Ein klares Verständnis der Gesamtziele vermitteln',
        'Aufgeschlossen sein und Beiträge aus allen Quellen wertschätzen',
        'Demokratisch sein und Teilnahme fördern',
        'Verantwortung für Entscheidungen übernehmen',
        'Volle Hingabe an die Aufgabe zeigen',
        'Andere durch hohe Standards motivieren',
      ],
      teamLeaderFlipSide: [
        'Manchmal als unambitioniert, zurückhaltend oder wenig durchsetzungsstark wahrgenommen werden',
        'Manchmal als reserviert oder distanziert erscheinen',
        'Nicht innehalten, um Informationen zu sammeln oder Alternativen zu prüfen',
        'Kann zu kontrollierend wirken',
      ],
      conflicts: [
        'Könnten sich von Menschen gestört fühlen, die sie als inkompetent wahrnehmen',
        'Könnten frustriert sein über Menschen, die sich nicht für die Teamziele engagieren',
        'Häufig gestört von Menschen, die eine lockere Haltung zeigen und zu unorganisiert wirken',
        'Schwierigkeiten mit Menschen, die ein Thema weiter diskutieren, obwohl bereits Klarheit besteht',
        'Könnten genervt sein von Menschen, die sich in irrelevante Detaildiskussionen verlieren',
        'Konflikte wahrscheinlich mit Menschen, die keinen systematischen Umgang mit Zeit und Ressourcen haben',
      ],
      stressAdvisor: [],
      workEnvironment: ['Strukturiert und organisiert', 'Zukunftsorientiert', 'Zielorientiert'],
      underStress: [
        'Könnten hyperkritisch gegenüber sich selbst und anderen werden',
        'Könnten wütend und kontrollierend werden, ohne die Auswirkungen auf andere zu berücksichtigen',
      ],
      underSevereStress: [
        'Könnten sich gefangen und übermäßig emotional fühlen',
        'Könnten unschuldige Kommentare als persönliche Angriffe verstehen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, weniger kritisch und akzeptierender gegenüber Stärken und Schwächen anderer zu sein',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Organisator, Der Ideenlieferant, Der Kritiker',
    withOthersParagraphs: {
      strengths: [
        'Gesellig und aufgeschlossen',
        'Logik und Analyse sind zentral',
        'Kritisch und hinterfragend',
        'Bereit, sich selbst und andere herauszufordern',
        'Durchsetzungsfähig, entscheidungsfreudig',
        'Kommuniziert direkt',
        'Organisiert gerne Menschen und übernimmt Verantwortung',
        'Zuverlässig und vertrauenswürdig',
        'Legt Wert auf Gerechtigkeit',
      ],
      weakSpotsWithOthers: [
        'Weniger durchsetzungsfähige Personen könnten sie als dominant, meinungsstark oder dogmatisch wahrnehmen',
        'Könnten als selbstüberheblich erscheinen',
        'Manche Menschen könnten ihre Tendenz, die Führung zu übernehmen, missbilligen',
        'Könnten kühl und unpersönlich wirken',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ENTJ genannt. Einige zugehörige Stichworte sind Planer, offen oder enthusiastisch.',
    typeKeywords: [
      '1. Planer',
      '2. Visionär',
      '3. Organisator',
      '4. Logisch',
      '5. Analytisch',
      '6. Theoretiker',
      '7. Nonkonformist',
      '8. Streitbar',
      '9. Herzlich und offen',
      '10. Entscheidungsfreudig',
      '11. Objektiv',
      '12. Enthusiastisch',
    ],
    typePersonalStrengths: [
      'Zielorientiert, organisiert und liefert Ergebnisse wie geplant',
      'Langfristige Perspektive bei Problemlösungen, Fokus auf das große Ganze',
      'Erkennt unlogische und ineffiziente Verfahren und Richtlinien',
      'Analysiert und bringt logische Ordnung in Probleme',
      'Entwickelt neue Theorien/Modelle/Ideen/Systeme zur Problemlösung',
      'Kritisiert potenzielle Lösungen objektiv',
      'Implementiert Lösungen',
      'Bringt Energie in Aufgaben ein',
      'Lernt Neues, erweitert Wissen und gibt es weiter',
      'Präsentiert Ideen',
      'Gibt fachkundige Ratschläge',
      'Fokussiert auf das, was sein könnte, nicht auf das, was ist',
      'Konzeptionelles und abstraktes Denken',
      'Trifft Entscheidungen',
      'Setzt Wissen gezielt ein',
    ],
    typeWeakSpots: [
      'Kann manchmal selbstbewusster und positiver erscheinen, als es die Erfahrung zulässt',
      'Kann manchmal dominant und kontrollierend wirken',
      'Könnte zu sehr drängen, um Aufgaben sofort zu erledigen',
      'Könnte andere irritieren, weil alles sofort erledigt werden soll',
    ],
    description:
      'Der ENTJ ist eine ehrliche, entscheidungsfreudige Person. Sie sind ergebnisorientiert und organisiert, mit Blick auf das große Ganze und langfristige strategische Planung. ENTJs sind oft sehr charismatisch und können andere dazu bringen, ihrem Führungsstil zu folgen. Sie sind direkt und zielgerichtet, vermeiden zu enge persönliche Bindungen. ENTJs streben nach Effizienz und geben selten auf, um jedes Hindernis zu überwinden.',
    individualTypeByLetters: 'Führungskraft',
    individualDescription:
      'Als Führungskraft sind sie wahrscheinlich ehrliche und entscheidungsfreudige Personen. Sie sind ergebnisorientiert und organisiert, mit Blick auf das große Ganze und langfristige strategische Planung. Sie sind wahrscheinlich charismatisch und können andere motivieren, ihnen zu folgen. Sie handeln direkt und zielgerichtet, ohne zu eng persönliche Bindungen einzugehen. Sie streben nach Effizienz und geben selten auf, um Hindernisse zu überwinden.',
  },
  ENTP: {
    shortDescription: 'Visionär',
    atWorkParagraphs: {
      effectiveness: [
        'Ihr Einfallsreichtum einbringen, ohne andere mit zu vielen Möglichkeiten zu überfordern',
        'Nicht die Energie auf zu viele Projekte gleichzeitig verteilen',
        'Selbstdisziplin entwickeln',
        'Analytische Fähigkeiten nutzen, um die Richtung zu finden, die sie verfolgen möchten',
        'Entscheidungsfähigkeiten verbessern',
        'Sich daran erinnern, dass die Umsetzung einer Idee komplizierter ist als das Vorstellen',
        'Nachverfolgung verbessern, indem sie Notizen über ihre Versprechen führen',
        'Anderen öfter die Bühne überlassen und sie glänzen lassen',
        'Nicht alles auf ein konzeptionelles Modell reduzieren',
        'Auf Informationen achten, die nicht Teil eines logischen Bereichs sind',
        'Alternative Möglichkeiten priorisieren, ggf. mit stärkerem Fokus auf Menschen',
        'Akzeptieren, dass sie nicht alles wissen können',
        'Zeitmanagement-Techniken verbessern',
        'Interpersonelle Bedürfnisse von Kollegen öfter berücksichtigen',
        'Mehr auf praktische Aspekte konzentrieren',
      ],
      teamLeader: [
        'Visionär sein und mühelos neue Ideen entwickeln',
        'Andere motivieren, indem hohe Standards gesetzt werden',
        'Erwarten, dass andere sich wirklich herausfordern und über die geforderten Aufgaben hinaus Neues schaffen',
        'Die besten Leute einbeziehen und inspirieren, mit ihnen zu arbeiten',
        'Die Interessen der Teammitglieder in einen Handlungsplan einfließen lassen',
        'Ein dynamisches Zusammenspiel von Prozess und Inhalt zulassen',
        'Verschiedene Möglichkeiten hinterfragen und kritisch prüfen',
        'Verschiedene Perspektiven und Informationen anbieten, um die beste, objektivste Lösung zu finden',
      ],
      conflicts: [
        'Wahrscheinlich genervt von Menschen, die sich weigern, neue Möglichkeiten zu betrachten',
        'Könnten sich gestört fühlen von Menschen, die nicht verstehen, dass Prozess und Inhalt gleichermaßen wichtig sind',
        'Wahrscheinlicher genervt von Menschen, die Wesentliches nicht vom Unwesentlichen trennen können',
        'Könnten Konflikte mit Menschen haben, denen eine leistungsorientierte Einstellung fehlt',
      ],
      stressAdvisor: [
        'Übernimmt zu viele Projekte, die nicht abgeschlossen werden können',
        'Vermeidet Details, wird unruhig und hektisch',
        'Zieht sich schließlich aus Situationen zurück und benötigt viel Unterstützung',
      ],
      workEnvironment: [
        'Weniger Wiederholungen und Routine, viele Gelegenheiten zur Kommunikation und Zusammenarbeit mit anderen',
        'Erfordert Vorstellungskraft und Innovation, wo Ideen diskutiert werden',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, ihre Energie nicht auf zu viele Projekte gleichzeitig zu verteilen',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Ideenlieferant, Der Umsetzer oder Der Kritiker',
    withOthersParagraphs: {
      strengths: [
        'Gesellig und kontaktfreudig',
        'Anregende Gesellschaft, aufmerksam und offen',
        'Hinterfragend und schnell zu kritischer Auseinandersetzung',
        'Strahlt Energie und Humor aus',
        'Ansteckende Begeisterung',
        'Spontan',
        'Sympathisch und freundlich',
        'Kann zum Spaß auf beiden Seiten einer Diskussion argumentieren',
        'Schätzt andere aufgrund von wahrgenommener Intelligenz und Fähigkeit, nicht wegen Status',
        'Kann verschiedene Perspektiven erkennen',
        'Gut im Einschätzen anderer Menschen',
        'Flexibel, anpassungsfähig und aufgeschlossen',
        'Locker und ungezwungen',
        'Nonkonformistisch, individualistisch',
      ],
      weakSpotsWithOthers: [
        'Könnte manchmal unsensibel gegenüber emotionalen Themen wirken',
        'Könnte andere abschrecken durch Vorliebe für Debatten und intellektuelles Ringen',
        'Hohes Bedürfnis nach Autonomie',
        'Könnte andere mit Ideenfluten überfordern',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ENTP genannt. Einige zugehörige Stichworte sind visionär, unternehmerisch oder leistungsorientiert.',
    typeKeywords: [
      '1. Logisch',
      '2. Einfallsreich',
      '3. Freidenkend',
      '4. Begeistert',
      '5. Visionär',
      '6. Möglichkeiten',
      '7. Blick fürs große Ganze',
      '8. Unternehmerisch',
      '9. Einfallsreich',
      '10. Anpassungsfähig',
      '11. Kompetent',
      '12. Leistungsorientiert',
    ],
    typePersonalStrengths: [
      'Schnell, logisch, einfallsreich und vielseitig',
      'Denkt: "Wenn sie es sich vorstellen können, können sie es auch tun"',
      'Löst gerne komplexe Probleme und sieht sie als Herausforderung',
      'Exzellent im Erkennen von Mustern und im Überblick über das große Ganze',
      'Exzellent im Aufdecken grundlegender Prinzipien eines Problems',
      'Gut in der Lösung von Problemen mit langfristigen Lösungen',
      'Stellt sich Möglichkeiten vor und analysiert sie strategisch, um den effektivsten Handlungsweg zu bestimmen',
      'Oft talentiert im Erkennen von Trends und Vorhersagen',
      'Hohe Leistungsorientierung, genießen Herausforderungen für sich und andere',
      'Gut darin, andere durch hohe Standards zu motivieren',
      'Kann improvisieren und auf die Bedürfnisse des Moments reagieren',
      'Von Inspiration getrieben',
      'Natürlicher Konzeptualisierer',
      'Arbeitet in Energieschüben und Begeisterung',
      'Kann ein dynamischer Visionär, ein Architekt der Zukunft sein',
      'Gut im Führen von Veränderungsprozessen',
      'Hohe intellektuelle Neugier',
      'Gut im objektiven Beobachten',
      'Glaubt nicht an Regeln und navigiert um sie herum, um Ziele zu erreichen',
      'Selbstständig und unabhängig von Autorität',
      'Kann eigenständig arbeiten',
      'Gut im Anwenden von Wissen und Kompetenzen',
      'Innovator, Planer und Gestalter, der Komplexität liebt',
      'Starke Fähigkeiten in der Entwicklung neuer Theorien, Modelle, Ideen oder Systeme',
      'Sprachgewandt',
      'Gut im Analysieren mit Logik und Empathie',
      'Origineller Denker',
      'Kann gut argumentieren',
      'Ewiger Optimist',
      'Gut im Formulieren und Klarstellen von Ideen mit kreativen Einsichten',
      'Gut in Analyse und Synthese',
    ],
    typeWeakSpots: [
      'Ungeduldig bei langsamen Abläufen',
      'Nimmt sich zu viele Aufgaben gleichzeitig vor',
      'Langweilt sich bei Routine und wenig herausfordernder Arbeit, wiederholt selten dasselbe auf gleiche Weise',
      'Legt hohen Wert auf Kompetenz und Leistung, erwartet zu viel von anderen',
      'Setzt wahrscheinlich hohe Standards für sich selbst, die zu Selbstzweifeln führen können',
    ],
    description:
      'Der ENTP ist ein Pionier von Wandel und Einfallsreichtum. Flexibilität und Innovation treiben sie an, sie sind gesellig und diskutieren gerne Ideen und Konzepte, oft als „Advocatus Diaboli“, um andere zum Nachdenken über neue Wege zu bringen. Energetisch und gesprächig suchen sie neue Umgebungen, um intellektuelle Herausforderungen zu entdecken. Sie mögen keine Monotonie und vermeiden Wiederholungen, um Abwechslung und Spannung in Aufgaben zu bringen.',
    individualTypeByLetters: 'Visionär',
    individualDescription:
      'Als Visionär sind sie wahrscheinlich Pioniere von Wandel und Einfallsreichtum. Sie gedeihen durch Flexibilität und streben nach Innovation. Sie sind gesellig, genießen Debatten über Ideen und Konzepte, spielen möglicherweise den Advocatus Diaboli, um andere zu neuen Sichtweisen zu bringen. Energetisch und gesprächig suchen sie neue Umgebungen, um intellektuelle Herausforderungen zu entdecken. Die Angst vor Monotonie und Wiederholung führt dazu, dass sie sich leicht verzetteln, und sie versuchen Dinge anders zu machen, um Abwechslung und Spannung zu erzeugen.',
  },
  ESFJ: {
    shortDescription: 'Versorger',
    atWorkParagraphs: {
      effectiveness: [
        'Sich daran erinnern, präziser und auf den Punkt zu sein',
        'Lernen, Kritik oder Ablehnung nicht persönlich zu nehmen, sondern die objektiven Fakten dahinter anzuerkennen',
        'Nicht zu schnell im Namen anderer handeln',
        'Andere ihre eigenen Fehler machen lassen',
        'Sich daran erinnern, ein Thema nicht totzureden und die trivialen Dinge leichter loszulassen',
        'Weniger Gewicht auf Konsens legen',
        'Akzeptieren, dass manchmal eine Neubewertung getroffener Entscheidungen notwendig ist, wenn neue Informationen auftauchen, selbst wenn dies einen Neustart bedeutet',
        'Akzeptieren, dass Pläne und Prozesse nicht in Stein gemeißelt sind',
        'Offener für die Intuition anderer sein',
        'Akzeptieren, dass Ungewissheit Teil des Lebens ist und Entscheidungen manchmal unter Unsicherheit getroffen werden müssen',
      ],
      teamLeader: [
        'Eine klare Agenda setzen und Konsens und Abschluss durch Einhaltung der Zeitvorgaben erreichen',
        'Eine Atmosphäre schaffen, in der jeder die eigene Bedeutung für das Team versteht und ermutigt wird, beizutragen',
        'Kooperativ erarbeiten, was getan werden muss und wer am besten für die Aufgaben geeignet ist',
        'Andere durch überzeugende Argumente motivieren, die die Gefühle der Menschen berücksichtigen',
        'Sich auf praktische Wege konzentrieren, um das Team bei der Problemlösung zusammenzubringen',
        'Offene Fragen klären, indem die richtigen Fragen gestellt werden, anstatt alle Antworten selbst zu geben',
      ],
      conflicts: [
        'Könnte Konflikte mit Personen haben, die sie als passiv wahrnehmen',
        'Wahrscheinlicher Konflikt mit Personen, die keine Verantwortung für Problemlösungen übernehmen',
        'Könnte sich über Menschen ärgern, die sich nicht an die Agenda halten und Zeit verschwenden',
        'Könnte gestört sein von Menschen, die den Konsens stören, indem sie den Advocatus Diaboli spielen',
        'Wahrscheinlich geringe Toleranz gegenüber Menschen, die den Fortschritt durch ständiges Unterbrechen verlangsamen',
        'Wahrscheinlicher Konflikt mit Menschen, die Aufgaben nicht genau und rechtzeitig erledigen',
      ],
      stressAdvisor: [],
      workEnvironment: [],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, anderen ihre eigenen Fehler machen zu lassen',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      'Sie fühlen sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Organisator, Der Umsetzer, Der Präzise',
    withOthersParagraphs: {
      strengths: [
        'Warmherzig, freundlich und unterstützend',
        'Gesprächig, beliebt',
        'Fürsorglich',
        'Harmonisch',
        'Hat ein gutes Verantwortungsbewusstsein und ist altruistisch',
        'Respektvoll, wohlerzogen',
        'Sympathischer Charakter, der Entscheidungen auf Grundlage von Werten trifft',
        'Nachdenklich',
        'Starkes Pflichtbewusstsein',
        'Loyal gegenüber Freunden und Kollegen',
        'Traditionen wertschätzend',
      ],
      weakSpotsWithOthers: [
        'Nimmt objektive Kritik oft persönlich',
        'Mag Konflikte überhaupt nicht, meidet Auseinandersetzungen',
        'Besonders auf Anerkennung bedacht',
        'Kann zu starr sein und passt sich Veränderungen schwer an',
        'Stellt sich Beleidigungen vor, wo keine beabsichtigt waren',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ESFJ genannt. Einige zugehörige Stichworte sind harmonisch, sympathisch oder ordentlich',
    typeKeywords: [
      '1. Harmonie',
      '2. Warmherzig',
      '3. Ordentlich',
      '4. Kooperativ',
      '5. Fürsorglich',
      '6. Detailorientiert',
      '7. Sympathisch',
      '8. Pflichtbewusst',
      '9. Bodenständig',
      '10. Systematisch und organisiert',
      '11. Plant voraus',
    ],
    typePersonalStrengths: [
      'Positive und optimistische Einstellung',
      'Tröstende Fürsorge bieten',
      'Gut im Schlichten von Konflikten',
      'Loyal gegenüber etablierten Systemen und bewahrt Traditionen',
      'Nimmt Arbeit ernst und erwartet, dass andere dies ebenfalls tun',
      'Gewissenhaft und sehr aufmerksam bei Details',
      'Bringt Wärme in alles, was sie tun',
      'Setzt auch kleine Dinge zuverlässig um',
      'Aufmerksam, erkennt, was andere im Alltag brauchen und versucht, dies bereitzustellen',
      'Guter Teamplayer, kooperativ',
      'Schafft ein harmonisches Umfeld',
      'Organisiert die Welt um sich herum',
      'Erledigt Aufgaben pünktlich und in der geforderten Qualität',
      'Realistische, faktenbasierte Sichtweise',
      'Definiert zielorientierte Arbeitsweisen im Team',
      'Verantwortungsbewusster Arbeitsansatz, findet Wege, nützlich zu sein',
      'Gut im Detailplanen',
      'Bietet systematische, praktische Perspektiven',
      'Organisiert und motiviert andere zur Mitarbeit',
      'Inklusiv und erkennt unterschiedliche Ideen an',
      'Fördert Harmonie durch Organisation, Enthusiasmus und Humor',
      'Großes Interesse an Dingen, die direkt das Leben der Menschen beeinflussen',
      'Starke Kundenorientierung, lässt Menschen wertgeschätzt fühlen',
    ],
    typeWeakSpots: [
      'Starkes Bedürfnis nach Harmonie, neigt dazu Konflikte zu vermeiden',
      'Arbeitet am besten mit Ermutigung und Lob, könnte sich zurückhalten, wenn dies fehlt',
      'Geringes Interesse an abstrakten Konzepten ohne direkten Bezug zu Menschen',
      'Benötigt eine geordnete Arbeitsumgebung',
      'Mag Veränderungen, Instabilität und Krisen nicht',
      'Unterstützt bestehende Ordnung, eventuell zu abhängig von Autorität',
      'Sehnt sich nach Zugehörigkeit, kann sich entmutigt fühlen, wenn Wertschätzung fehlt',
      'Könnte eigenen Status oder Seniorität zu hoch bewerten',
      'Benötigt klar definierte Teamrolle und Ziele',
      'Detailorientierung könnte von Kollegen als Mangel an Vorstellungskraft interpretiert werden',
    ],
    description:
      'Der ESFJ ist ein mitfühlender Mensch, der darauf fokussiert ist, andere zu unterstützen und Harmonie zu schaffen. Gewissenhaft und beziehungsorientiert, sieht er das Positive in Menschen. Extrem loyal und teamorientiert konzentriert er sich auf Ergebnisse und hilft anderen gerne. Der ESFJ möchte Harmonie im Team wahren und sucht Lösungen, die alle zufriedenstellen, ohne das Erledigen der Aufgaben zu gefährden.',
    individualTypeByLetters: 'Versorger',
    individualDescription:
      'Als Versorger sind sie wahrscheinlich mitfühlend, fokussiert auf die Unterstützung anderer und das Schaffen von Harmonie. Gewissenhaft und beziehungsorientiert sehen sie das Positive in Menschen. Sie sind extrem loyal, teamorientiert, konzentrieren sich auf Ergebnisse und helfen anderen gerne. Sie wahren Harmonie im Team, ohne das Erledigen der Aufgaben zu gefährden, und suchen Lösungen, die für alle zufriedenstellend sind.',
  },
  ESFP: {
    shortDescription: 'Darsteller',
    atWorkParagraphs: {
      effectiveness: [
        'Weniger Fokus auf das Hier und Jetzt, stattdessen mehr auf Aufgaben konzentrieren',
        'Besseres Verständnis für das „Big Picture“ entwickeln',
        'Das Gleichgewicht zwischen Lebendigkeit und Leichtfertigkeit finden',
        'Eigene Ansichten selbstbewusster ausdrücken',
        'Aufgabenbezogene Aspekte stärker betonen',
        'Mehr Durchsetzungsvermögen zeigen und „Nein“ zu unvernünftigen Anforderungen sagen',
        'Auf die detaillierten Anforderungen der Aufgaben achten',
        'Kritik nicht persönlich nehmen',
        'Den Wert logischer Analyse anerkennen',
      ],
      teamLeader: [
        'Mehr darauf achten, den gesamten Prozess in Gang zu setzen und getroffene Entscheidungen zusammenzufassen',
        'Die Zusammenarbeit im Team fördern und sicherstellen, dass die Stärken und Beiträge aller anerkannt werden',
        'Weite Teilnahme fördern und sicherstellen, dass alle Meinungen und Vorschläge berücksichtigt werden, bevor eine Entscheidung getroffen wird',
        'Andere ermutigen, positive Ergebnisse zu ermöglichen',
      ],
      conflicts: [
        'Könnte sich von Personen frustriert fühlen, die sie als übermäßig negativ, kritisch, beschuldigend oder pessimistisch wahrnehmen',
        'Könnte sich von Personen gestört fühlen, die hochtrabend sprechen, ohne einen klaren Punkt zu machen',
        'Könnte sich über Menschen ärgern, die nur auf die Aufgabe fokussiert sind und dabei die Menschen um sich herum ignorieren',
        'Schwierigkeiten mit Personen, die engstirnig sind und andere Sichtweisen oder Beiträge nicht wertschätzen',
      ],
      stressAdvisor: [],
      workEnvironment: ['Praktisch anpackend', 'Praxisnah', 'Energetisch und spaßig', 'Mit Freiheit und Raum', 'Nicht routinemäßig'],
      underSevereStress: [
        'Könnten als zu kompromissbereit wahrgenommen werden, zu sehr personalisieren und Details, Theorie oder das große Ganze vernachlässigen',
      ],
      weakSpotsWithOthers: [
        'Unter starkem Stress fällt es ihnen möglicherweise schwer, sich von einem Projekt zurückzuziehen, um praktische oder physische Bedürfnisse zu erfüllen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, ein besseres Verständnis für das „Big Picture“-Denken zu entwickeln',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      'Sie fühlen sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Umsetzer, Der Moderator, Der Brückenbauer',
    withOthersParagraphs: {
      strengths: [
        'Aufgeschlossen',
        'Warmherzig',
        'Freundlich',
        'Unterstützend',
        'Lebensfroh',
        'Spontan',
        'Tolerant',
        'Aufgeschlossen',
        'Lebendig',
        'Gesprächig',
        'Einfühlsam',
        'Taktvoll',
        'Kooperativ',
        'Egalitär',
        'Locker',
        'Auf die Bedürfnisse anderer bedacht',
      ],
      weakSpotsWithOthers: [
        'Manchmal zu kompromissbereit',
        'Manchmal zu gefühlsbetont',
        'Könnte als etwas unorganisiert wahrgenommen werden',
        'Kritik kann zu persönlich genommen werden',
        'Neigt dazu, Situationen zu überinterpretieren',
        'Könnte versuchen, zu viele Dinge gleichzeitig zu tun',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ESFP genannt. Einige zugehörige Stichworte sind energetisch, erlebnisorientiert oder realistisch',
    typeKeywords: [
      '1. Begeistert',
      '2. Optimistisch',
      '3. Handlungsorientiert',
      '4. Energetisch',
      '5. Pragmatiker',
      '6. Tolerant',
      '7. Emotional',
      '8. Realistisch',
      '9. Bodenständig',
      '10. Neue Aktivitäten, neue Menschen',
      '11. Erlebnisorientiert',
      '12. Anpassungsfähig',
    ],
    typePersonalStrengths: [
      'Hohe Energie',
      'Praktische Lösungen für konkrete, reale Probleme finden',
      'Lösungen entwickeln, die die Bedürfnisse und Gefühle anderer berücksichtigen',
      'Einfühlsam und verständnisvoll, bereit, andere Standpunkte zu berücksichtigen',
      'Nicht an alten Vorgehensweisen festhalten, sondern bereit zur Anpassung',
      'Zielgerichtet, bereit, den direkten Weg zum Erfolg zu gehen',
      'Bereit und motiviert, Dinge in die Tat umzusetzen',
      'Handlungsorientiert und umsetzungsfreudig',
      'Weiß, was vor sich geht, und beteiligt sich aktiv',
      'Bringt gesunden Menschenverstand und realistische Herangehensweise in die Problemlösung ein',
      'Gute Merkfähigkeit',
      'Reagiert gut in Krisen',
      'Gut im Schlichten von Konflikten',
      'Inklusiv',
      'Belebt andere durch positives Denken',
    ],
    typeWeakSpots: [
      'Könnte als zu persönlich wahrgenommen werden',
      'Scheint möglicherweise Dinge nicht ernst genug zu nehmen',
      'Kann andere durch Abwertung von abstrakter und konzeptioneller Debatte irritieren',
      'Neigt dazu, Konflikten auszuweichen',
      'Neigt dazu, Routine zu vermeiden und hat Schwierigkeiten, festgelegte Verfahren einzuhalten',
      'Wahrscheinlich verbringen sie nicht genug Zeit mit Theorien, sondern stürzen sich lieber in die Praxis',
      'Hohe Detailgenauigkeit fällt ihnen nicht natürlich',
    ],
    description:
      'Der ESFP genießt tendenziell ästhetisch ansprechende Dinge. Sie sind engagiert und optimistisch, fördern andere und achten besonders auf deren Bedürfnisse. Freundlich und praktisch, legen sie Wert auf Kooperation und Harmonie, genießen praktische Aktivitäten und bevorzugen Action und Abwechslung. Mit Hoffnung, Optimismus und Tatendrang bringen ESFPs eine kooperative und persönliche Atmosphäre in Situationen.',
    individualTypeByLetters: 'Darsteller',
    individualDescription:
      'Als Darsteller genießen sie Dinge, die ästhetisch ansprechend sind. Sie sind wahrscheinlich engagiert und optimistisch und fördern andere, wobei sie besonders auf deren Bedürfnisse achten. Freundlich und praktisch, legen sie Wert auf Kooperation und Harmonie, genießen praktische Aktivitäten und bevorzugen Action und Abwechslung. Mit Hoffnung, Optimismus und Tatendrang bringen sie eine kooperative und persönliche Atmosphäre in Situationen.',
  },
  ESTJ: {
    shortDescription: 'Wächter',
    atWorkParagraphs: {
      effectiveness: [
        'Sensibler auf die Bedürfnisse anderer achten',
        'Über Fakten hinaus das große Ganze betrachten',
        'Mehr auf andere hören und gegensätzlichen Ansichten mehr Gewicht geben',
        'Sicherstellen, dass genügend Informationen gesammelt wurden, bevor Entscheidungen getroffen werden',
        'Mehr delegieren und anderen ermöglichen, Führungsfähigkeiten zu entwickeln',
        'Verstehen, dass der Prozess entscheidend für ein gutes Ergebnis ist. Lernen, schneller als Schritt für Schritt vorzugehen',
        'Offener für alternative Sichtweisen sein, auch wenn sie zunächst wenig Sinn ergeben',
        'Persönliche sowie aufgabenbezogene Aspekte betonen',
        'Anderen genug Raum lassen, um innovativ zu sein und Kreativität zu fördern',
        'Takt und Diplomatie entwickeln',
        'Offener für Experimente sein',
        'Kritische Bewertung beim Brainstorming zurückstellen',
        'Toleranz gegenüber Unsicherheit erhöhen',
      ],
      teamLeader: [
        'Gut darin, Orientierung zu geben',
        'Durchsetzungsfähig bei der Umsetzung von Plänen',
        'Gut im Management von Verhalten nach strikten Prinzipien',
        'Detaillierte, ergebnisorientierte Pläne entwickeln',
        'Gut darin, Probleme zu definieren und zu klären',
        'Jedem Teammitglied klar definierte Aufgaben und Verantwortung geben',
        'Das Team ständig herausfordern, ihr Bestes zu geben',
        'Sicherstellen, dass Regeln und Richtlinien eingehalten werden',
        'Hohe Energie und Engagement zeigen',
        'Gut darin, Dinge erledigt zu bekommen',
      ],
      conflicts: [
        'Könnte sich über Teammitglieder ärgern, die ihrer Meinung nach unkonzentriert sind, vom Thema abweichen oder Zeit verschwenden',
        'Könnte sich über unvorbereitete Teammitglieder ärgern, die Aufgaben nicht nachverfolgen',
        'Könnte frustriert sein über Meetings, die ihrer Ansicht nach keine klaren Ziele, Agenda oder Organisation haben',
        'Unzufrieden bei Rollen ohne klare Definition oder Ziele',
        'Könnte mit Teammitgliedern kollidieren, die oft zu spät, abwesend oder passiv sind',
      ],
      underStress: [
        'Unter Stress: Könnte hyperkritisch gegenüber sich selbst und anderen werden',
        'Fühlt sich möglicherweise nicht wertgeschätzt oder nimmt harmlose Kommentare persönlich',
        'Unter großem Stress: Kann wütend und kontrollierend werden, Anweisungen erteilen, ohne Konsequenzen für andere zu bedenken',
      ],
      stressAdvisor: [
        'Unter Stress: Könnte hyperkritisch gegenüber sich selbst und anderen werden',
        'Fühlt sich möglicherweise nicht wertgeschätzt oder nimmt harmlose Kommentare persönlich',
        'Unter großem Stress: ESTJs können wütend und kontrollierend werden, Anweisungen geben, ohne die Folgen für andere zu bedenken',
      ],
      workEnvironment: [
        'Um Konzentration auf Details und Pläne zu ermöglichen, mit klar definierten Verantwortlichkeiten',
        'Wo Menschen häufig präsent sind und Möglichkeiten haben, Verantwortung zu übernehmen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, sensibler auf die zwischenmenschlichen Bedürfnisse anderer zu achten.',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Umsetzer, Der Organisator oder Der Präzise',
    withOthersParagraphs: {
      strengths: [
        'Gesellig, lebhaft und aufgeschlossen',
        'Selbstbewusst und sicher',
        'Schnell beim Äußern eigener Ansichten',
        'Direkt, klar und pragmatisch',
        'Verantwortungsbewusst und zuverlässig',
        'Schätzt Effizienz und Effektivität bei anderen',
        'Kritisiert offen andere, die ihre Standards nicht erfüllen',
        'Bereit, andere zu beaufsichtigen, um praktische Ordnung sicherzustellen',
        'Proaktiv bei neuen sozialen Kontakten',
        'Zäh und belastbar',
        'Komfortabel beim Erteilen von Anweisungen',
      ],
      weakSpotsWithOthers: [
        'Verständnis für die Gefühle anderer fällt ihnen möglicherweise schwer',
        'Könnte als zu direkt wahrgenommen werden',
        'Kann zu kontrollierend wirken, Entscheidungen für andere treffen, ohne Zustimmung einzuholen',
        'Starkes Bedürfnis nach Sicherheit erschwert die Akzeptanz anderer Perspektiven',
        'Könnte als autoritär wahrgenommen werden',
        'Neigt dazu, Entscheidungen zu schnell zu treffen',
        'Könnte den Eindruck erwecken, Entscheidungen nur zur Klärung zu treffen',
        'Könnte unflexibel erscheinen',
        'Menschlicher Faktor könnte vernachlässigt werden',
        'Könnte als überkritisch und ungeduldig wahrgenommen werden',
        'Nicht inklusiv genug',
        'Fokus auf Details kann als Mangel an Vorstellungskraft gesehen werden',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ESTJ genannt. Einige zugehörige Stichworte sind entscheidungsfreudig, logisch oder leitend',
    typeKeywords: [
      '1. Entscheidungsfreudig',
      '2. Zäh',
      '3. Geordnet',
      '4. Präzise',
      '5. Verantwortlich',
      '6. Vorgehensweisen',
      '7. Logisch',
      '8. Leitend',
      '9. Organisiert',
      '10. Faktenbasiert',
      '11. Analytisch',
      '12. Prinzipientreu',
    ],
    typePersonalStrengths: [
      'Gut darin, zu analysieren und Ordnung zu schaffen',
      'Gut darin, Projekte und Menschen zu organisieren',
      'Gewissenhaft und arbeitet energisch, um Aufgaben zu erledigen',
      'Starker Fokus auf sofort sichtbare Ergebnisse auf effizienteste Weise',
      'Pragmatisch, realistisch, praktisch',
      'Löst Probleme direkt und pragmatisch',
      'Starkes logisches Denken',
      'Starker Arbeitsethos',
      'Gute Routinepflege',
      'Stark in Planung',
      'Entscheidungsfreudig und schnell in der Umsetzung',
      'Gut im Dokumentieren von Verfahren und Informationen',
      'Exzellente Detailgenauigkeit, gut im Finden und Korrigieren von Fehlern',
      'Verbessert Dinge Schritt für Schritt',
      'Übernimmt gerne Verantwortung',
      'Traditionstreu, loyal gegenüber Systemen und Strukturen',
      'Zuverlässig, nimmt Verantwortung ernst',
      'Gut im Zusammenstellen von Informationen',
      'Gut im Durchsetzen von Regeln und Richtlinien',
      'Realistischer Ausblick, klarer Fokus auf nächste Schritte',
      'Systematisch und folgt logischen Standards',
      'Gut darin, Vorsicht walten zu lassen',
      'Methodische und systematische Herangehensweise',
    ],
    typeWeakSpots: [
      'Geringe Toleranz für Unsicherheit',
      'Unwillig, bestehende Strukturen und Traditionen zu ändern, bevorzugt Stabilität',
      'Fokus auf das unmittelbare kann langfristige Konsequenzen vernachlässigen',
      'Unflexibel bei Planänderungen',
      'Starker Detailfokus kann das Denken im großen Ganzen einschränken',
      'Kreativität könnte durch Ausschluss von Ideen ohne sofortigen Nutzen eingeschränkt werden',
      'Überschätzt Hierarchie und Autorität',
      'Vergisst manchmal die Gefühle und Perspektiven anderer bei Entscheidungen',
      'Manchmal übervorsichtig',
    ],
    description:
      'Der ESTJ ist energisch und zielorientiert. Nicht unbedingt flexibel, bevorzugt der ESTJ Planung und Zweck, übernimmt gern Verantwortung. Sie verlassen sich auf erprobte Methoden, Fakten und systematische Prozesse. Als extrovertierter Typ sind sie gern im Mittelpunkt und diskutieren ihre Ideen. Mit klarer Richtung und Plan können ESTJs Ordnung aus Chaos schaffen.',
    individualTypeByLetters: 'Wächter',
    individualDescription:
      'Als Wächter sind sie energisch und zielorientiert. Nicht unbedingt flexibel, bevorzugen Planung und Zweck und übernehmen gern Verantwortung. Sie verlassen sich auf erprobte Methoden, Fakten und systematische Prozesse. Als extrovertierter Typ sind sie gern im Mittelpunkt und diskutieren ihre Ideen. Mit klarer Richtung und Plan sind sie in der Lage, Ordnung aus Chaos zu schaffen.',
  },
  ESTP: {
    shortDescription: 'Macher',
    atWorkParagraphs: {
      effectiveness: [
        'Aufbauen auf ihrer Stärke, Fakten zu präsentieren und realistisch zu bleiben',
        'Weniger auf Improvisation vertrauen (obwohl sie darin sehr gut sind) und mehr auf Vorbereitung',
        'Mehr Zeit nehmen, bevor gehandelt wird',
        'Ihren guten Sinn für Humor nutzen, sich aber der negativen Wirkung sarkastischer Bemerkungen bewusst sein',
        'Krisen vermeiden, auch wenn sie insgeheim den Adrenalinkick mögen',
        'Bereitschaft zum Kompromiss zeigen, wenn es dem Team hilft',
        'Ihre Zuhörfähigkeiten üben',
        'Innovation bei anderen akzeptieren und fördern',
        'Den zwischenmenschlichen Bedürfnissen von Kollegen mehr Aufmerksamkeit schenken',
      ],
      teamLeader: [
        'Dem Team klare Richtung und Zielsetzung geben',
        'Das Problem artikulieren, Meinungen sammeln, Alternativen diskutieren und praktische Entscheidungen treffen',
        'Das Team effektiv zusammenarbeiten lassen',
        'Die Teammitglieder zu Aktion motivieren',
        'Schnell auf Veränderungen reagieren und Strategie anpassen',
        'Nicht wertend gegenüber den Vorschlägen anderer sein, solange sie logisch sind',
      ],
      conflicts: [
        'Kann sich über Teammitglieder ärgern, die passiv oder unengagiert wirken',
        'Geringe Toleranz gegenüber Personen, die sie als unkonzentriert oder unrealistisch ansehen',
        'Probleme mit Teammitgliedern, die den logischen Weg nicht sehen und sich von Nebenzielen ablenken lassen',
        'Könnte sich über Leute ärgern, die zu viel jammern, klagen oder negativ sind',
        'Könnte sich über Menschen ohne Humor ärgern',
      ],
      stressAdvisor: [
        'Regeln zunehmend ablehnen und wettbewerbsorientiert werden',
        'Zu viele Projekte übernehmen, spontan und sogar risikoreich handeln',
        'Vor dem Sprechen nachdenken, um nicht als rücksichtslos oder unfreundlich zu wirken',
      ],
      workEnvironment: [
        'Bevorzugt Möglichkeiten für Flexibilität und neue Ideen',
        'Mag es nicht, durch zu viele Regeln eingeschränkt zu werden',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, sanfter in die Handlung überzugehen',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Vernetzer, Der Macher oder Der Moderator',
    withOthersParagraphs: {
      strengths: [
        'Gesellig und kontaktfreudig',
        'Spontan und enthusiastisch',
        'Aufgeschlossen und tolerant',
        'Guter Sinn für Humor',
        'Geschätzt für bodenständigen Realismus',
        'Begeistert',
        'Abenteuerlustig und lebensfroh',
        'Durchsetzungsfähig',
      ],
      weakSpotsWithOthers: [
        'Könnte andere unbeabsichtigt verletzen, indem er Meinungen direkt äußert',
        'Oft zu bereit, Systeme zu verwerfen, die anderen Stabilität bieten',
        'Könnte ungeduldig mit den zwischenmenschlichen Aspekten einer Aufgabe sein',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird ESTP genannt. Einige zugehörige Stichworte sind logisch, faktenorientiert oder anpassungsfähig',
    typeKeywords: [
      '1. Spontan',
      '2. Aufgeschlossen',
      '3. Handlungsorientiert',
      '4. Logisch',
      '5. Praktisch',
      '6. Improvisatorisch',
      '7. Problemlöser',
      '8. Abenteuerlustig',
      '9. Energisch',
      '10. Humorvoll',
      '11. Anpassungsfähig',
      '12. Faktenbasiert',
    ],
    typePersonalStrengths: [
      'Gut in praktischer Problemlösung vor Ort',
      'Natürlicher Problemlöser, gut im Beseitigen von Hindernissen',
      'Positive Einstellung und begeisterter Tatendrang',
      'Gut in Krisensituationen',
      'Anpassungsfähig, tolerant und flexibel',
      'Stark handlungsorientiert und motiviert andere zum Handeln',
      'Realistisch und bodenständig',
      'Gut in logischer Entscheidungsfindung',
      'Gut im Umgang mit realen Dingen, die bearbeitet, zerlegt oder zusammengebaut werden können',
      'Knüpft schnell neue Beziehungen',
      'Bereit, etablierte Systeme und Protokolle zu umgehen, um Lösungen zu finden',
    ],
    typeWeakSpots: [
      'Mag keine langen theoretischen Erklärungen',
      'Langweilt sich bei zu viel Stabilität',
      'Handelt oft zu schnell',
      'Detailaufgaben werden vernachlässigt',
      'Lernt lieber durch praktische Erfahrung als durch theoretische Reflexion',
    ],
    description:
      'Der ESTP liebt Aktion und schnelle Umgebungen. Er ist enthusiastisch, charmant und direkt. Fokus auf praktische Aktivitäten und Planung, schätzt Abwechslung und Herausforderungen, hat Schwierigkeiten mit monotonen Aufgaben. ESTPs sollten vorsichtig sein, nicht zu viele Risiken einzugehen oder zu viele Projekte gleichzeitig zu übernehmen. Rationalität wird meist vor Gefühlen anderer priorisiert, Fokus auf greifbare Details.',
    individualTypeByLetters: 'Macher',
    individualDescription:
      'Als Macher liebt er Action und schnelle Umgebungen. Er ist enthusiastisch, charmant und direkt. Fokus auf praktische Aktivitäten und Planung, genießt Abwechslung und Herausforderungen, hat Schwierigkeiten mit monotonen Aufgaben. Als Macher sollte er vorsichtig sein, nicht zu viele Risiken gleichzeitig einzugehen. Rationalität wird vor den Gefühlen anderer priorisiert, greifbare Details stehen im Vordergrund.',
  },
  INFJ: {
    shortDescription: 'Beschützer',
    atWorkParagraphs: {
      effectiveness: [
        'Ihre persönlichen Gedanken, Gefühle und Werte freier ausdrücken',
        'Nicht zu starr an einer Idee festhalten',
        'Netzwerkfähigkeiten entwickeln und lockere Freundschaften schließen',
        'Die Bedeutung von Small Talk erkennen',
        'Sicherstellen, dass sie die Gegenwart nicht ignorieren, weil sie zu sehr auf zukünftige Möglichkeiten fokussiert sind',
        'Lernen, sich selbst besser zu verkaufen',
        'Techniken zur Durchsetzungsfähigkeit üben',
        'Sich daran erinnern, dass Menschen eine Idee eher umsetzen, wenn sie die Möglichkeit zur Mitgestaltung hatten',
        'Erkennen, dass es wichtig ist, sich in Stresssituationen nicht von Menschen zurückzuziehen',
        'Keine unerreichbaren Standards für sich selbst oder andere setzen',
      ],
      teamLeader: [
        'Das große Ganze sehen und Visionen entwickeln',
        'Mit Teammitgliedern individuell umgehen und erwarten, dass sie ihre Aufgaben erfüllen',
        'Das Team mit kreativen und intuitiven Einsichten stimulieren',
        'Mit der Mehrheit Kompromisse schließen, es sei denn, es widerspricht den eigenen Werten',
        'Interpersonelle Dramen innerhalb der Gruppe erfolgreich managen',
        'Die Gruppendynamik einschätzen und Übersichten sowie detaillierte Zusammenfassungen geben, wie es weitergeht',
        'Sitzungen zum Abschluss bringen',
      ],
      conflicts: [
        'Wird wahrscheinlich von Menschen frustriert, die sie als feindlich, ungeduldig oder pessimistisch wahrnehmen oder die andere nicht wertschätzen',
        'Probleme mit Menschen, die voreilige und irrelevante Ratschläge geben und nicht bereit sind, sich eingehend mit Problemen zu befassen',
        'Kann von sehr gesprächigen Personen genervt sein',
        'Kann sich an Menschen stören, die nicht zum Arbeitsprozess beitragen',
        'Konflikte möglich mit Personen, deren Fokus ausschließlich auf Inhalten liegt und die keinen Wert auf Prozesse legen',
      ],
      stressAdvisor: [
        'Kann übermäßig kooperativ werden und das Bedürfnis haben, ständig zu gefallen',
        'Findet Kritik schwer zu verarbeiten',
        'Hat das Bedürfnis, Probleme alleine zu lösen',
      ],
      workEnvironment: [
        'Wo es Möglichkeiten gibt, am großen Ganzen zu arbeiten, kreativ zu sein und flexibel agieren zu können',
        'Wo weniger Routineaufgaben und feste Zeitpläne vorhanden sind',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, nicht zu starr an Ideen festzuhalten',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich wahrscheinlich am wohlsten in den folgenden Rollen im Team: Der Präzise, Der Ideengeber oder Der Moderator',
    withOthersParagraphs: {
      strengths: [
        'Ruhig, freundlich und gesellig',
        'Legt großen Wert auf Menschen und Beziehungen in ihrem Leben',
        'Mitfühlend',
        'Still und bestimmt',
        'Emotional tiefgründig',
        'Öffnet sich anderen selten, bevor sie sie gut kennen',
        'Sensibel für die Bedürfnisse anderer',
        'Genießt Harmonie zwischen Menschen',
        'Fürsorglich und rücksichtsvoll',
        'Feste Prinzipien',
      ],
      weakSpotsWithOthers: [
        'Überempfindlich gegenüber Konflikten',
        'Braucht Ermutigung und Anerkennung',
        'Benötigt Zeit für zwischenmenschliche Höflichkeiten',
        'Könnte Kritik sehen, wo keine gemeint ist',
        'Könnte zu vorsichtig sein und nicht spontan handeln',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname-Typ wird INFJ genannt. Einige zugehörige Stichworte sind ausdauernd, fürsorglich, freundlich',
    typeKeywords: [
      '1. Prinzipientreu',
      '2. Kreativ',
      '3. Nachdenklich',
      '4. Organisiert',
      '5. Ausdauernd',
      '6. Gewissenhaft',
      '7. Mitfühlend',
      '8. Freundlich',
      '9. Fürsorglich',
      '10. Verantwortungsbewusst',
      '11. Harmonisch',
    ],
    typePersonalStrengths: [
      'Gut darin, Probleme aus globaler Perspektive zu betrachten',
      'Gute Glaubwürdigkeit durch Ideen und Einsichten vermitteln',
      'Andere durch positive, humorvolle und tatkräftige Herangehensweise inspirieren',
      'Gut in konzeptionellem und abstraktem Denken',
      'Oft talentiert im kreativen Ausdruck',
      'Gewissenhafte Arbeitsweise',
      'Strukturiert und methodisch',
      'Starke Aufmerksamkeit fürs Detail',
      'Setzt sich voll für die Arbeit ein',
      'Hartnäckig im Streben nach Erfolg',
      'Gut im Setzen von Zielen',
      'Erledigt Aufgaben und tut, was nötig oder gewünscht ist',
      'Entscheidungsfreudig bei der Umsetzung ihrer Vision',
      'Starkes Interesse am Gemeinwohl',
      'Gut darin, durch Beziehungen Veränderungen zu fördern',
      'Gut im Lehren und Helfen beim Verstehen',
      'Starkes Pflicht- und Verantwortungsbewusstsein',
      'Erkennt die Bedeutung der Einhaltung von Regeln',
      'Erledigt Aufgaben termingerecht und auf hohem Niveau',
      'Gut im Verständnis theoretischer Prinzipien hinter Problemen',
    ],
    typeWeakSpots: [
      'In sozialen Situationen offener werden',
      'Entscheidungen besser abwägen: wann gründlich durchdenken, wann schneller handeln',
      'Weniger konfliktscheu sein und weniger auf Konsens fixiert',
      'Sich wohler fühlen beim Kommunizieren mit größeren Gruppen',
      'Das Unbehagen schwieriger, unpopulärer Entscheidungen annehmen',
    ],
    description:
      'Der INFJ legt Wert auf Mitgefühl und Zusammenarbeit, ist ein guter Problemlöser durch Innovation und Organisationsfähigkeit. Als introvertierter Typ ist der INFJ sensibel und zurückhaltend, behält Gefühle meist für sich. Gleichzeitig zeigt er großes Mitgefühl und Fürsorge. Durch den Wunsch, Dinge organisiert abzuschließen, kann er Aufgaben effektiv erledigen, ohne seine einfühlsame Herangehensweise zu verlieren. Für INFJs ist es extrem wichtig, das Gefühl zu haben, Dinge zu verbessern.',
    individualTypeByLetters: 'Beschützer',
    individualDescription:
      'Als Beschützer legt %fname Wert auf Mitgefühl und Zusammenarbeit. %fname ist wahrscheinlich ein guter Problemlöser, durch Innovation und Leidenschaft, Dinge organisiert und erledigt zu bekommen. Als introvertierter Typ ist %fname sensibel und zurückhaltend, behält Gefühle meist für sich. Gleichzeitig zeigt %fname großes Mitgefühl und Fürsorge. Kombiniert mit dem Wunsch, Dinge organisiert zu halten, kann %fname Aufgaben erledigen, ohne die persönliche, einfühlsame Herangehensweise zu verlieren. Das Gefühl, Dinge zu verbessern, ist extrem wichtig für %fname.',
  },
  INFP: {
    shortDescription: 'Idealist',
    atWorkParagraphs: {
      effectiveness: [
        'Lernen und üben, wie man durchsetzungsfähig ist',
        'Sich nicht von Menschen zurückziehen, wenn es stressig wird',
        'Zeitmanagement und Priorisierung verbessern',
        'Erkennen, dass andere schnelle Ergebnisse schätzen, auch wenn diese noch nicht ideal sind',
        'Eine gesunde Wertschätzung für Details und Praktisches entwickeln',
        'Den Wert von lockeren Freundschaften und Networking erkennen',
        'Probleme nicht überkomplizieren oder überintellektualisieren',
        'Erkennen, dass man nicht jedem gefallen muss',
        'Vorausplanen',
        'Mehr Entschlossenheit zeigen',
        'Andere mit Ideen über Möglichkeiten des großen Ganzen begeistern',
      ],
      teamLeader: [
        'Inspirierende Motivation bieten',
        'Auf gemeinsame Ideale und zugrunde liegende Themen konzentrieren',
        'Teamgeist und Gruppenarbeit durch positive Atmosphäre fördern',
        'Nach Gruppen-Konsens suchen, um Ziele zu erleichtern',
        'Starkes Bewusstsein für Stärken und Schwächen der Menschen haben',
        'Menschen dazu anregen, ihr Potenzial auszuschöpfen',
        'Menschen herausfordern und stimulieren',
        'Ihr Team ermutigen, neue Möglichkeiten zu sehen',
        'Hohes Maß an Moral und Kooperation aufrechterhalten, durch ihre Fähigkeit, andere „lesen“ zu können',
      ],
      conflicts: [
        'Könnte Probleme mit Menschen haben, die sich zu ernst nehmen',
        'Kann sich an Personen stören, die zu viele Details verlangen oder deren technische Logik das Verständnis erschwert',
        'Kann sich an Menschen stören, die nicht zugeben, wenn sie etwas nicht verstehen',
        'Kann sich an Menschen stören, die nicht alle Meinungen berücksichtigen',
      ],
      stressAdvisor: [
        'Zunehmendes Bedürfnis nach Perfektionismus',
        'Fokussiert sich nur auf das, was sie für wichtig halten',
        'Verliert Realismus und Einfachheit bei der Aufgabenbewältigung',
      ],
      workEnvironment: ['Zukunftsorientiert', 'Spiegelt eigene Werte wider', 'Erlaubt Arbeiten in Energieschüben'],
      underStress: [],
      underSevereStress: [],
    },
    atWorkTagLine: 'Sehr teamorientiert',
    atWorkSneakPeek:
      'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, zu lernen und zu üben, wie man durchsetzungsfähig ist',
    withOthersTagLine: '---',
    withOthersSneakPeek: '---',
    withOthersParagraphs: {
      strengths: [
        'Freundlich',
        'Tolerant',
        'Kooperativ',
        'Unterstützend',
        'Mitfühlend',
        'Geheimnisvoll',
        'Großzügig mit positivem Feedback',
        'Rücksichtsvoll, nachdenklich und fürsorglich',
        'Sehr loyal gegenüber den Menschen, die einem wichtig sind',
        'Akzeptierend',
        'Echtes Interesse an anderen',
      ],
      weakSpotsWithOthers: [
        'Benötigt Ermutigung und Anerkennung',
        'Bevorzugt harmonische Beziehungen und meidet Konflikte',
        'Wird ungesellig, wenn zu sehr in eigene Arbeit vertieft',
        'Nimmt zu viele Aufgaben gleichzeitig an',
        'Geht nicht gut mit Menschen um, die ihre persönlichen Werte bedrohen',
        'Überempfindlich gegenüber Gefühlen anderer und der Stimmung im Team',
      ],
    },
    typeTagLine: 'Offen, frei denkend, intellektuell neugierig',
    typeSneakPeek: 'Ihr Typ wird INFP genannt. Sie neigen dazu, nicht gut mit Menschen umzugehen, die ihre persönlichen Werte bedrohen',
    typeKeywords: [
      '1. Offen',
      '2. Frei denkend',
      '3. Intellektuell neugierig',
      '4. Lernbegierig',
      '5. Fantasievoll',
      '6. Optimistisch',
      '7. Wertorientiert',
      '8. Selbstentwickelnd',
      '9. Entdecker',
      '10. Unterstützend',
      '11. Nachdenklich',
      "12. 'Big Picture'",
    ],
    typePersonalStrengths: [
      'Voller Enthusiasmus',
      'Flexibel und anpassungsfähig',
      'Unabhängiger Arbeitsstil',
      'Verantwortungsbewusst',
      'Sehr teamorientiert',
      'Starker Teamgeist',
      'Bereit, zum Gemeinwohl beizutragen',
      'Einsichtig und kreativ',
      'Kümmert sich intensiv um alles, woran sie beteiligt sind',
      'Schnell im Erkennen von Möglichkeiten',
      'Katalysator, um Ideen in die Realität umzusetzen',
      'Fähig, viele Dinge kurz vor der Frist zu erledigen',
      'Gut darin, Veränderungen zu fördern',
      'Aktiver Beitrag zur Teamharmonie',
    ],
    typeWeakSpots: [
      'Nicht zu perfektionistisch oder zu idealistisch sein',
      'Bodenständiger werden',
      'Persönliche Werte lockern, wenn sie von anderen nicht geteilt werden',
    ],
    description:
      'Der INFP ist wahrscheinlich warmherzig und mitfühlend und wird von Herzensangelegenheiten angetrieben, für die er kämpft. Die Arbeit mit persönlichen Werten ist für den INFP extrem wichtig, und wenn er nicht von einem Ideal oder dem Streben nach tieferem Sinn motiviert ist, neigt er zu träumen und aufzuschieben. INFPs sind neugierig und offen, arbeiten flexibel und genießen Teamarbeit, um neue Ideen einzubringen und anderen zu helfen. Einmal für eine Sache gewonnen, sind INFPs äußerst loyal und engagiert.',
    individualTypeByLetters: 'Idealist',
    individualDescription:
      'Als Idealist ist %fname wahrscheinlich warmherzig und mitfühlend, von Herzensangelegenheiten angetrieben und kämpft für seine Überzeugungen. Die Arbeit mit persönlichen Werten ist für %fname extrem wichtig. Wenn %fname nicht von einem Ideal oder tieferem Sinn motiviert ist, neigt %fname zu träumen und aufzuschieben. %fname ist neugierig, offen und flexibel, arbeitet gern im Team, um neue Ideen einzubringen und andere zu fördern. Einmal für eine Sache gewonnen, ist %fname äußerst loyal und engagiert.',
  },
  INTJ: {
    shortDescription: 'Wissenschaftler',
    atWorkParagraphs: {
      effectiveness: [
        'Netzwerken als notwendigen Teil des Berufslebens akzeptieren, einschließlich aktiver Kontaktaufnahme und gelegentlichem Smalltalk',
        'Diplomatisch Feedback geben, damit andere nicht zu defensiv reagieren',
        'Nicht versuchen, alles selbst zu machen (auch wenn sie denken, dass sie es besser könnten), sondern mehr delegieren',
        'Auf zwischenmenschliche Bedürfnisse achten – auch die eigenen',
        'Sicherstellen, dass andere genügend Raum haben, um in den Prozess einzubringen',
        'Klar kommunizieren, dass sie die Beiträge anderer schätzen',
        'Nicht zu sehr darauf bestehen, alle Informationen vor einer Entscheidung zu haben, sondern akzeptieren, dass manche Entscheidungen spontan getroffen werden sollten',
      ],
      teamLeader: [
        'Das Team mit starken Ideen und Überzeugungen zusammenhalten',
        'Dem Team helfen, klar zu definieren, was erreicht werden soll',
        'Sicherstellen, dass das Team seine Ziele erreicht und auf Kurs bleibt',
        'Das Team motivieren, indem hohe Standards gesetzt werden',
        'Dem Team eine effiziente und strukturierte Arbeitsumgebung bieten',
        'Konflikte objektiv und logisch vermitteln',
        'Das große Ganze im Auge behalten und die Strategie bei Bedarf ändern',
      ],
      teamLeaderFlipSide: [
        'Kann als zu streitlustig, zu aufgabenorientiert oder zu kontrollierend wahrgenommen werden',
        'Kann auch als zu zurückhaltend und distanziert erscheinen',
      ],
      conflicts: [
        'Kann Probleme mit Personen haben, die vereinbarte Aufgaben nicht abschließen',
        'Kann sich an Personen stören, die nicht genug Aufmerksamkeit schenken oder deren Fragen/ Vorschläge nicht ernst nehmen',
        'Kann sich an Personen stören, die nicht schnell genug auf Ideen oder Vorschläge reagieren',
        'Wird wahrscheinlich von Personen frustriert, die sich auf unwichtige Details konzentrieren oder sofortige Antworten verlangen',
      ],
      workEnvironment: [
        'Bietet Unabhängigkeit',
        'Legt großen Wert auf Effizienz',
        'Strukturiert und aufgabenorientiert',
        'Nicht routinemäßig, bietet jedoch Möglichkeit, Dinge zu ändern und neu zu gestalten',
        'Schafft intellektuelle Anregung',
      ],
      stressAdvisor: [
        'Kann zunehmend unflexibel und kritisch werden',
        'Kann starr in Meinungen und Standpunkten sein, schwer akzeptieren, wenn sie falsch liegen',
        'Kann ungeduldig werden und Fehler suchen, auch wenn keine vorhanden sind',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, nicht alles selbst zu machen',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      'Sie fühlen sich möglicherweise am wohlsten in folgenden Rollen im Team: Der Präzise, Der Ideenfinder, Der Kritiker',
    withOthersParagraphs: {
      strengths: [
        'Unabhängig',
        'Skeptisch',
        'Kritisch',
        'Hohe Ansprüche an Kompetenz und Leistung bei sich selbst und anderen',
        'Ruhig',
        'Beobachtend',
        'Entschlossen',
        'Prinzipientreu',
        'Argumentativ',
        'Zuverlässig',
        'Nachdenklich',
        'Logisch',
      ],
      weakSpotsWithOthers: [
        'Kann manchmal zu introspektiv sein',
        'Das Verständnis für die Gefühle anderer fällt nicht immer leicht',
        'Als ruhig wahrgenommen, kann starke Meinungsäußerung Kollegen überraschen',
        'Kann manchmal zu stur sein',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Ihr Typ wird INTJ genannt. Andere könnten sie als beharrlich, fürsorglich oder freundlich beschreiben',
    typeKeywords: [
      '1. Logisch',
      '2. Kompetent',
      '3. Pflichtbewusst',
      '4. Zielorientiert',
      '5. Angetrieben',
      '6. Ideenreich',
      '7. Methodisch',
      '8. Organisiert',
      '9. Zuverlässig',
      '10. Individualistisch',
      '11. Prinzipientreu',
    ],
    typePersonalStrengths: [
      'Gut im Geben von Ratschlägen',
      'Setzt Dinge um',
      'Zielorientiert',
      'Erreicht geplante Ergebnisse',
      'Denkweise mit langfristiger Perspektive, Probleme mit langfristigen Lösungen angehen',
      'Starkes Pflicht- und Verantwortungsbewusstsein',
      'Stark in systematischem, strukturiertem Denken',
      'Stark in objektiver, logischer Analyse',
      'Kann selbstständig lernen',
      'Methodischer und organisierter Ansatz zur Problemlösung',
      'Hohe Standards für sich und andere setzen',
      'Fähig, schwierige Entscheidungen zu treffen',
      'Originell',
      'Fähig, Dinge zu Ende zu bringen',
      'Gut in der Entwicklung neuer Theorien, Modelle, Ideen oder Systeme',
      'Aufgaben qualitativ hochwertig und termingerecht erledigen',
      'Gute Einhaltung etablierter Methoden und Verfahren',
      'Detailbewusst',
      'Arbeitet mit theoretischen Konzepten und Systemen',
      'Zuverlässig',
      'Nonkonformistisch',
      'Intellektuell neugierig',
    ],
    typeWeakSpots: [
      'Kann andere irritieren, wenn sie zu sehr auf ein Ziel oder eine Aufgabe fokussiert sind',
      'Bevorzugt, selbst verantwortlich zu sein, und übernimmt manchmal eine schwerere Last, als nachhaltig ist',
    ],
    description:
      'Der INTJ ist wahrscheinlich schnell und einfallsreich mit strategischem Verstand. Sie verbringen gerne Zeit damit, Informationen sorgfältig zu analysieren. System und Strategie sind wichtig, wobei Organisation und Planung helfen, Abläufe zu verstehen. Abstrakte Ideen und Logikprobleme reizen den INTJ, der Zeit mit Analyse und Hinterfragen verbringt. Der Fokus liegt auf dem Greifbaren und Rationalen; INTJs besitzen Vision und Innovationskraft und arbeiten zielgerichtet. Emotionen werden selten geteilt.',
    individualTypeByLetters: 'Wissenschaftler',
    individualDescription:
      'Als Wissenschaftler ist %fname wahrscheinlich schnell und einfallsreich mit strategischem Verstand. %fname verbringt gerne Zeit damit, Informationen sorgfältig zu analysieren. System und Strategie sind wichtig, Organisation und Planung helfen, Abläufe zu verstehen. Abstrakte Ideen und Logikprobleme reizen %fname, der Zeit mit Analyse und Hinterfragen verbringt. Der Fokus liegt auf dem Greifbaren und Rationalen; %fname besitzt Vision und Innovationskraft und arbeitet zielgerichtet. Emotionen teilt %fname selten mit anderen.',
  },
  INTP: {
    shortDescription: 'Denker',
    atWorkParagraphs: {
      effectiveness: [
        'Bewusst öfter auf Menschen zugehen',
        'Den Wert von Smalltalk erkennen',
        'Klarer und einfacher sprechen',
        'Dinge auf eine logische Hypothese reduzieren',
        'Häufiger die Gefühle anderer in Analysen und Aufgaben berücksichtigen',
        'Zeitmanagement-Techniken erlernen',
        'Auf praktische Aspekte konzentrieren',
      ],
      teamLeader: [
        'Verwendet Logik und Vernunft, um das Team in die richtige Richtung zu lenken',
        'Richtet Rahmenbedingungen ein, die es allen Teammitgliedern ermöglichen, ihre Fähigkeiten einzubringen',
        'Wird als Wissens- und Kompetenzquelle gesehen, als aktiver Ansprechpartner für andere',
        'Setzt die Agenda und hält andere fokussiert',
        'Bietet Optionen, damit Entscheidungen unterschiedliche Meinungen berücksichtigen und die beste Lösung erzielt wird',
        'Fasst zusammen und generalisiert geschickt',
        'Motiviert das Team durch das Setzen hoher Standards',
      ],
      conflicts: [
        'Kann sich an Teammitgliedern stören, die unkonzentriert und unlogisch sind',
        'Findet Teammitglieder herausfordernd, die aus Unwissenheit streiten',
        'Kann frustriert sein, wenn objektive Kommentare persönlich genommen werden',
        'Hat Schwierigkeiten mit Menschen, die keine Vision oder kein Engagement für das Projekt zeigen',
        'Geringe Toleranz für Leute, die viel reden und sich auf unwesentliche Details konzentrieren',
      ],
      stressAdvisor: [
        'Kann sarkastisch und ungeduldig werden',
        'Kann zunehmend überanalysieren und endlose Debatten führen',
        'Kann in Standpunkten starr werden; wird unabhängiger und negativer',
      ],
      workEnvironment: ['Eine Umgebung, die Ideen und Kreativität fördert', 'Wenig Unterbrechungen und minimale Aufsicht'],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, bewusst öfter auf Menschen zuzugehen.',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich möglicherweise am wohlsten in folgenden Rollen im Team: Der Ideenfinder, Der Kritiker, Der Organisator',
    withOthersParagraphs: {
      strengths: [
        'Fordert sich selbst und andere gerne heraus',
        'Spricht lieber über Ideen als Smalltalk zu machen',
        'Aufgeschlossen, frei denkend und tolerant',
        'Legt Wert auf Logik und rationales Denken',
        'Kann enthusiastisch sein',
        'Freundlich und einfühlsam',
        'Nachdenklich',
        'Kommuniziert bevorzugt schriftlich',
        'Wird oft als tiefgründiger Denker gesehen',
      ],
      weakSpotsWithOthers: [
        'Kann zunächst ruhig und zurückhaltend wirken',
        'Kann zu streitlustig erscheinen',
        'Kann kühl und verschlossen wirken',
        'Wird manchmal als unpraktisch, unorganisiert und zerstreut wahrgenommen',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Ihr Typ wird INTP genannt. Einige Schlüsselwörter: Visionär, Wettbewerbsorientiert, Analytisch',
    typeKeywords: [
      '1. Visionär',
      '2. Kompetent',
      '3. Neugierig',
      '4. Experte',
      '5. Wissensreich',
      '6. Logisch',
      '7. Begeistert',
      '8. Wettbewerbsorientiert',
      '9. Nachdenklich',
      '10. Flexibel',
      '11. Analytisch',
      '12. Überblick behalten',
    ],
    typePersonalStrengths: [
      'Gut im Anwenden von Wissen',
      'Gut im Lösen von Problemen mit langfristigen Lösungen',
      'Hohe Leistungsbereitschaft',
      'Wird wahrscheinlich Experte in einem Bereich',
      'Besitzt oft ungewöhnliche Fähigkeiten zur Lösung komplexer Probleme',
      'Muster, Zusammenhänge und das große Ganze gut erkennen',
      'Stark in der Entwicklung neuer Theorien, Modelle, Ideen oder Systeme',
      'Bietet kritische Perspektive und intellektuelle Einsicht',
      'Starkes konzeptionelles Problemlösungsvermögen',
      'Sammelt Daten objektiv',
      'Hoch analytisch',
      'Kreativ und einfallsreich',
      'Hinterfragt und kritisiert bestehende Ordnung',
      'Flexibel und anpassungsfähig',
      'Genießt kreative Möglichkeiten',
      'Komplexität liegt ihnen',
      'Unabhängig',
      'Architekt des Wandels',
      'Systemgestalter',
    ],
    typeWeakSpots: [
      'Weniger Skepsis und Kritik zeigen',
      'Offener und kommunikativer sein',
      'Andere Interessen erkunden; Beruf, der Interessen nutzt',
      'Mehr Menschen akzeptieren, die nicht so kompetent sind',
      'In manchen Situationen konformer sein',
      'Gelegentlich Selbstzweifel',
      'Arbeitet möglicherweise lieber allein',
      'Gut im Finden kreativer Lösungen, weniger begeistert bei der Umsetzung',
    ],
    description:
      'Der INTP denkt und löst Probleme gerne, da er ein gutes Auge für Details hat und abstrakte Ideen und logische Probleme versteht. INTPs sind meist ruhig und nachdenklich, behalten Gefühle für sich. Als objektive und realistische Personen führen sie gerne Debatten, hinterfragen und suchen die Wahrheit, können jedoch manchmal vage wirken oder Bedürfnisse anderer übersehen, wenn sie auf das Wesentliche fokussiert sind.',
    individualTypeByLetters: 'Denker',
    individualDescription:
      'Als Denker denkt %fname gerne nach und löst Probleme, hat ein gutes Auge für Details und versteht abstrakte Ideen und logische Probleme. %fname ist wahrscheinlich ruhig und nachdenklich, behält Gefühle für sich. Als objektive und realistische Person führt %fname gerne Debatten, hinterfragt und sucht die Wahrheit. %fname kann jedoch manchmal vage wirken oder die Bedürfnisse anderer übersehen, wenn zu sehr auf das Wesentliche fokussiert.',
  },
  ISFJ: {
    shortDescription: 'Fürsorger',
    atWorkParagraphs: {
      effectiveness: [
        'Nicht zu empfindlich sein und sich nicht zu sehr um Harmonie sorgen',
        'Eigene sowie die Fähigkeiten und Beiträge anderer öfter loben, statt sie herunterzuspielen',
        'Bereit sein, wenn nötig auch schwierige Botschaften zu übermitteln',
        'Bemerkungen anderer nicht zu ernst oder persönlich nehmen, sondern öfter vorbeiziehen lassen',
        'Bei Überzeugung an der eigenen Meinung festhalten, statt der Mehrheit nur wegen Harmonie zu folgen',
      ],
      teamLeader: [
        'Arbeitet hart und ist immer gut vorbereitet',
        'Hört auf alle Ideen, bringt alle zusammen und gewinnt Unterstützung für die Lösung',
        'Lässt andere innerhalb des Systems zusammenarbeiten und hält sich an gesetzte Regeln',
        'Ermutigt andere, ihre Führungsfähigkeiten zu entwickeln, wann immer möglich',
        'Besteht auf Präzision und Genauigkeit',
      ],
      conflicts: [
        'Kann sich an Teammitgliedern stören, die zu viel reden, unterbrechen oder nicht zuhören',
        'Kann sich an Teammitgliedern stören, die andere Menschen und Ideen abwerten',
        'Kann irritiert sein von Menschen, die zu viel Zeit auf persönliche Agenden verwenden und Teamaufgaben vernachlässigen',
        'Könnte Konflikte mit Personen haben, die ihrer Meinung nach wenig gesunden Menschenverstand zeigen',
        'Wird wahrscheinlich Schwierigkeiten mit Menschen haben, die nicht bereit sind, zusammenzuarbeiten oder relevante Informationen zu teilen',
      ],
      stressAdvisor: [
        'Findet es sehr schwer, Nein zu sagen, und wird zunehmend pessimistisch, wenn Dinge nicht nach Plan laufen',
        'Wird zunehmend ängstlich und dadurch starrer in ihrem Denken, um die Situation zu kontrollieren',
        'Kann sich entmutigt fühlen, wenn sie keine Anerkennung bekommen',
      ],
      workEnvironment: [
        'Bevorzugt eine harmonische Umgebung, um Konflikte zu vermeiden',
        'Bevorzugt eine strukturierte Organisation mit definierten Rollen und Aufgaben',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit, die Arbeitseffektivität zu steigern, besteht darin, bereit zu sein, wenn nötig auch schwierige Botschaften zu übermitteln.',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich im Team möglicherweise am wohlsten in folgenden Rollen: Der Präzise, Der Organisator, Der Kritiker',
    withOthersParagraphs: {
      strengths: [
        'Freundlich und verantwortungsbewusst',
        'Rücksichtsvoll und fürsorglich, immer für andere da',
        'Stille Wärme und sanfter Humor',
        'Erinnert sich an Details über wichtige Menschen',
        'Gut darin, Konflikte zu glätten',
        'Loyal, rücksichtsvoll und aufmerksam',
        'Sorgt sich um das Wohlbefinden anderer',
        'Leise nachdenklich, empathisch und sensibel',
        'Legt Wert auf Präzision in Gesprächen und erwartet dasselbe von anderen',
      ],
      weakSpotsWithOthers: [
        'Starkes Bedürfnis nach Stabilität',
        'Kann überempfindlich sein',
        'Konzentriert sich vielleicht zu sehr auf das Hier und Jetzt',
        'Starke Abneigung gegen Konflikte, daher große Vorliebe für harmonische Beziehungen',
        'Empfindlich gegenüber den Gefühlen anderer und der Stimmung im Team',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: "%fname's Typ wird ISFJ genannt. Einige Schlüsselwörter: Praktisch, Ordentlich, Präzise",
    typeKeywords: [
      '1. Praktisch',
      '2. Systematisch',
      '3. Zuverlässig',
      '4. Realistisch',
      '5. Fürsorglich',
      '6. Sensibel',
      '7. Detailliert',
      '8. Ordentlich',
      '9. Entscheidungsfreudig',
      '10. Freundlich',
      '11. Stabil',
      '12. Präzise',
    ],
    typePersonalStrengths: [
      'Bereit, sofort zu handeln, wenn nötig',
      'Praktisch und mit gesundem Menschenverstand',
      'Gut darin, Aufgaben zu hoher Qualität abzuschließen',
      'Arbeitet ernsthaft und mit starkem Verantwortungsbewusstsein',
      'Realistischer Beobachter der Umgebung',
      'Lernt bevorzugt durch Tun',
      'Starke Aufmerksamkeit für Details',
      'Gutes Gedächtnis und fundiertes Faktenwissen',
      'Geduldig, um alle notwendigen Details zu sammeln',
      'Gute Organisationsfähigkeiten',
      'Bringt Stabilität in Projekte oder Gruppen',
      'Systematische, gründliche Herangehensweise',
      'Entscheidungsfreudig',
      'Hoch loyal, wenn Zugehörigkeit vermittelt wird',
      'Gut darin, Ordnung zu schaffen',
      'Gibt Informationen sorgfältig und zuverlässig weiter',
    ],
    typeWeakSpots: [
      'Offener oder freundlicher sein',
      'Konflikte mehr akzeptieren und nicht immer Harmonie suchen',
      'Weniger autoritätsabhängig sein',
      'Mehr Veränderung zulassen',
      'Akzeptieren, dass Unordnung und Unsicherheit manchmal unangenehm sind',
    ],
    description:
      'Der ISFJ ist wahrscheinlich eine fürsorgliche und mitfühlende Person, die sich nützlich und hilfreich fühlen möchte. Sie sind engagiert und geduldig und geben nicht beim ersten Hindernis auf. Gleichzeitig sind sie eher schüchtern und ruhig. Der ISFJ denkt an andere, bevor er an sich selbst denkt, bietet praktische Unterstützung und bevorzugt einen persönlichen Ansatz. Sie kümmern sich gerne um alltägliche Details und ziehen es vor, nicht im Mittelpunkt zu stehen.',
    individualTypeByLetters: 'Fürsorger',
    individualDescription:
      'Als Fürsorger wird %fname wahrscheinlich als fürsorglich und mitfühlend wahrgenommen. %fname möchte sich nützlich und hilfreich fühlen, ist engagiert und geduldig und gibt nicht beim ersten Hindernis auf. %fname ist wahrscheinlich eher schüchtern und ruhig, denkt an andere vor sich selbst und bietet praktische Unterstützung, bevorzugt einen persönlichen Ansatz. %fname kümmert sich wahrscheinlich um die alltäglichen Details und zieht es vor, nicht aufzufallen.',
  },
  ISFP: {
    shortDescription: 'Künstler',
    atWorkParagraphs: {
      effectiveness: [
        'Nicht zu bescheiden sein und Anerkennung annehmen, wenn sie verdient ist',
        'Eigene Fähigkeiten und Beiträge nicht herunterspielen',
        'Kommentare anderer nicht zu ernst nehmen, erkennen, dass manche Menschen nicht nachdenken, bevor sie sprechen',
        'Weiterhin freundlich sein, aber lernen, bei Bedarf klare Grenzen zu setzen',
        'Nicht zu sehr auf Harmonie bedacht sein',
        'Lernen, schwierige Botschaften zu übermitteln, wenn nötig',
        'Nicht zu selbstkritisch sein',
        'Zeitmanagement-Techniken lernen',
        'Lernen, „Nein“ zu sagen',
        'Negative Rückmeldungen üben',
      ],
      teamLeader: [
        'Inspiriert andere, ihrem Arbeitsethos zu folgen: hart arbeiten und stets vorbereitet sein',
        'Hört sorgfältig alle Ideen und Bedenken an und gewinnt durch sanfte, aber effektive Überzeugung die Unterstützung für eine Lösung',
        'Zeigt aufrichtiges Interesse an anderen Sichtweisen',
        'Erlaubt anderen viel Freiheit, eigene Ziele zu setzen',
        'Trifft Entscheidungen nur, wenn Konsens erreicht wurde',
      ],
      conflicts: [
        'Kann sich an Menschen stören, die zu viel reden',
        'Kann mit Menschen aneinandergeraten, die andere abwerten',
        'Kann irritiert sein von Personen ohne gesunden Menschenverstand',
        'Kann sich gestört fühlen von Menschen, die das Team zurückhalten, indem sie relevante Informationen nicht teilen',
        'Schätzt keine egoistischen oder unkooperativen Personen',
      ],
      stressAdvisor: [
        'Kann zunehmend unorganisiert werden, Schwierigkeiten mit Zeitmanagement haben und anderen Verantwortung überlassen, um eigene Verantwortung zu vermeiden',
        'Kann Kritik persönlich nehmen, auch wenn es nur unterschiedliche Meinungen sind',
        'Braucht Zeit für Entscheidungen und ändert häufig ihre Meinung',
      ],
      workEnvironment: [
        'Bevorzugt ein flexibles Umfeld mit wenig Aufsicht',
        'Bevorzugt eine unterstützende und sinnvolle Umgebung, um sich wohlzufühlen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, die eigenen Fähigkeiten und Beiträge nicht herunterzuspielen.',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname fühlt sich im Team möglicherweise am wohlsten in folgenden Rollen: Der Moderator, Der Organisator, Der Präzise',
    withOthersParagraphs: {
      strengths: [
        'Leise freundlich, warm und unterstützend',
        'Loyal und engagiert gegenüber wichtigen Menschen',
        'Sensibel',
        'Freundlich',
        'Aufgeschlossen',
        'Zeigt sanften Humor',
        'Nicht wertend oder meinungsstark',
        'Nachdenklich',
        'Kritisiert andere nicht unnötig',
        'Zwingt anderen nicht die eigenen Meinungen oder Werte auf',
      ],
      weakSpotsWithOthers: [
        'Kann etwas Zeit brauchen, um sich neuen Menschen zu öffnen',
        'Vermeidet Auseinandersetzungen',
        'Impulsiv',
        'Nicht sehr durchsetzungsfähig',
        'Kann sich unter Stress von Menschen zurückziehen',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: "%fname's Typ wird ISFP genannt. Einige Schlüsselwörter: Praktisch, Rücksichtsvoll, Flexibel",
    typeKeywords: [
      '1. Praktisch',
      '2. Aufgeschlossen',
      '3. Unabhängig',
      '4. Sanft',
      '5. Positiv',
      '6. Rücksichtsvoll',
      '7. Locker',
      '8. Praktisch tätig',
      '9. Flexibel',
      '10. Realistisch',
      '11. Loyal',
    ],
    typePersonalStrengths: [
      'Handelt lieber, als nur zu reden',
      'Sucht praktische, konkrete Lösungen für reale Probleme',
      'Tolerant, aufgeschlossen und akzeptiert andere Sichtweisen',
      'Kann selbstständig und nach eigenem Zeitplan arbeiten',
      'Flexibel und anpassungsfähig, hält sich nicht an veraltete Ideen',
      'Loyal und engagiert',
      'Gut darin, andere innerhalb des Systems zusammenarbeiten zu lassen',
      'Gibt Informationen sorgfältig und zuverlässig weiter',
      'Genießt praktische Erfahrungen',
      'Realistisch',
      'Gut im Krisenmanagement und Problemlösen',
      'Rücksichtsvoll und kann die Auswirkungen von Handlungen auf andere abschätzen',
      'Eigenständig denkend',
      'Positive Einstellung',
      'Gut darin, Inklusion zu fördern und Konflikte zu glätten',
      'Bevorzugt eine gleichberechtigte Arbeitsweise',
      'Hält an starken Werten und Idealen fest',
      'Gut in der Einzelkommunikation',
      'Sieht das größere Bild',
      'Kann Regeln umgehen, wenn nötig',
      'Gutes Beobachtungsvermögen',
    ],
    typeWeakSpots: [
      'Dringlichkeit erhöhen, Aufgaben schneller erledigen',
      'Offener sein oder Gedanken teilen',
      'Konflikte und Meinungsverschiedenheiten mehr schätzen; nicht immer Kompromisse suchen',
      'Routine und Vorschriften mehr schätzen, weniger Freiheit bevorzugen',
      'Weniger bescheiden in Bezug auf die eigenen Fähigkeiten sein',
      'Mehr Führungsinitiative übernehmen',
      'Auf Details mehr achten',
    ],
    description:
      'Der ISFP ist eine unkomplizierte, ruhige und freundliche Person. Sie sind kooperativ und bevorzugen eine unterstützende Rolle, um Teamharmonie zu fördern. Handlungsorientiert, bevorzugen sie praktische Aktivitäten, um ihre Kreativität einzusetzen, vermeiden jedoch Organisation und Planung und bevorzugen einen flexiblen, spontanen Ansatz, während sie Zukunftsprognosen meiden. Gute Zuhörer, tolerant und sensibel für die Bedürfnisse anderer, geben sie praktische Ratschläge und Unterstützung, wann immer möglich.',
    individualTypeByLetters: 'Künstler',
    individualDescription:
      'Als Künstler ist %fname wahrscheinlich eine unkomplizierte, ruhige und freundliche Person. %fname ist kooperativ und übernimmt gerne eine unterstützende Rolle, um Teamharmonie zu fördern. %fname ist handlungsorientiert und bevorzugt praktische Aktivitäten, um Kreativität einzusetzen. Planung und Organisation werden eher vermieden, Flexibilität und Spontanität stehen im Vordergrund, Zukunftsprognosen werden gemieden. %fname ist ein guter Zuhörer, tolerant und sensibel für die Bedürfnisse anderer und gibt praktische Unterstützung, wann immer möglich.',
  },
  ISTJ: {
    shortDescription: 'Inspektor',
    atWorkParagraphs: {
      effectiveness: [
        'Sorgt dafür, dass andere informiert und auf dem neuesten Stand bleiben',
        'Entwickelt ihre Networking-Fähigkeiten',
        'Versucht, angenehme Momente während des Arbeitstages zu schaffen',
        'Erkennt den Wert von Small Talk',
        'Fragt öfter nach Feedback',
        'Ermutigt und wertschätzt Kreativität',
        'Entwickelt zwischenmenschliche Kommunikationsfähigkeiten',
        'Erinnert sich daran, sich nicht zu sehr in Details zu verlieren und das Gesamtziel aus den Augen zu verlieren',
        'Blickt über bestehende Systeme hinaus',
        'Delegiert mehr und mikromanagt weniger',
        'Beteiligt sich frühzeitig an Gesprächen und bringt frühzeitig eigene Standpunkte ein',
        'Setzt kritisches Urteilen während Brainstormings aus',
      ],
      teamLeader: [
        'Gewinnt das Vertrauen des Teams durch logische Argumente, detailliertes Fachwissen und realistische Perspektive',
        'Ist offen für Fakten und logische Argumente',
        'Organisiert und erhält eine effektive Struktur, ohne viel sagen zu müssen',
        'Leitet sehr effiziente, fokussierte Meetings',
        'Ist klar in Bezug auf Erwartungen, Aufgaben und Standards',
      ],
      conflicts: [
        'Kann sich an Teammitgliedern stören, die zu gesprächig sind',
        'Kann irritiert sein von Personen, die als unzuverlässig wahrgenommen werden',
        'Hat wahrscheinlich eine geringe Toleranz für Menschen, die über zwischenmenschliche oder unwesentliche Themen diskutieren wollen',
      ],
      stressAdvisor: [
        'Kann besonders fokussiert und unflexibel werden und verlangen, dass Regeln und Strukturen eingehalten werden',
        'Fällt es schwer, eigene Fehler einzugestehen und anderen ausreichend Anerkennung zu geben',
        'Kann pessimistisch und übermäßig detailverliebt werden',
      ],
      workEnvironment: [
        'Bevorzugt Arbeit mit klaren Zielen und festgelegten Abläufen',
        'Arbeitet lieber an einer Aufgabe oder einem Projekt gleichzeitig, mit minimaler Aufsicht',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, den Wert von Small Talk zu erkennen.',
    withOthersTagLine: '---',
    withOthersSneakPeek: '%fname fühlt sich im Team am wohlsten in den Rollen: Der Kritiker, Der Präzise, Der Organisator',
    withOthersParagraphs: {
      strengths: [
        'Schätzt Traditionen und Loyalität',
        'Verfügt über Pflichtbewusstsein',
        'Bodenständig',
        'Cool und unabhängig',
        'Zuverlässig',
        'Hilfsbereit',
        'Standhaft in Meinungen',
        'Bevorzugt schriftliche Kommunikation gegenüber langen Diskussionen',
        'Behandelt Menschen respektvoll',
      ],
      weakSpotsWithOthers: [
        'Kann reserviert und unemotional wirken',
        'Nicht besonders kontaktfreudig, bevorzugt Ergebnisse über zwischenmenschliche Gespräche',
        'Schwer emotional Anschluss zu finden',
        'Kann als einfallslos und starr wahrgenommen werden',
        'Langsame Anpassung an Veränderungen',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: "%fname's Typ wird ISTJ genannt. Einige Schlüsselwörter: stabil, objektiv, entscheidungsfreudig",
    typeKeywords: [
      '1. Präzise',
      '2. Entscheidungsfreudig',
      '3. Rational',
      '4. Verantwortungsbewusst',
      '5. Respektvoll',
      '6. Stabil',
      '7. Detailorientiert',
      '8. Zuverlässig',
      '9. Methodisch',
      '10. Freundlich',
      '11. Informiert',
      '12. Objektiv',
    ],
    typePersonalStrengths: [
      'Ordentlich und organisiert, zu Hause und bei der Arbeit',
      'Verantwortungsbewusster Arbeitsansatz',
      'Engagiert, Aufgaben zu erledigen',
      'Erledigt Aufgaben gründlich',
      'Praktische, realistische Sichtweise',
      'Gut im Lösen von Problemen mit gesundem Menschenverstand',
      'Arbeitet kontinuierlich an Zielen, unabhängig von Ablenkungen',
      'Entscheidungsfreudig',
      'Objektiv und rational',
      'Starke analytische Fähigkeiten',
      'Engagiert gegenüber Menschen und Organisation',
      'Diszipliniert, mag Routinen entwickeln und folgen',
      'Präzise und hervorragendes Faktenwissen',
      'Exzellente Detailgenauigkeit',
      'Bereit, selbstständig zu arbeiten',
      'Legt Wert auf die Einhaltung etablierter Prozesse',
      'Gut im Durchsetzen von Regeln und Richtlinien',
      'Dokumentiert Verfahren und Informationen gut',
      'Verbessert Dinge schrittweise',
      'Hohe Qualitätsstandards, gut im Erkennen und Korrigieren von Fehlern',
      'Bringt hohe Verantwortlichkeit in jedes Team',
      'Fleißig und inspiriert andere, hart zu arbeiten, um Aufgaben pünktlich zu erledigen',
    ],
    typeWeakSpots: [
      'Mehr Instabilität und Unordnung akzeptieren',
      'Mehr Veränderung und Innovation zulassen',
      'Weniger regelgebunden sein',
    ],
    description:
      'Der ISTJ ist ein systematischer Mensch, besonders loyal und zuverlässig. Sie sind stolz auf ihre Arbeit und fühlen sich mit Routineaufgaben wohl. ISTJs sind ernsthafte, nachdenkliche Personen, die Ordnung schaffen und durchsetzen. Sie sind präzise, systematisch und verantwortungsbewusst und arbeiten gern mit Fakten. Verantwortung wird angenommen, Aufgaben werden strikt eingehalten, Unentschlossenheit und Faulheit werden kaum toleriert. Pflichtbewusstsein ist wichtig, und einmal engagiert, verfolgen sie Projekte konsequent und treffen Entscheidungen basierend auf Fakten und Logik.',
    individualTypeByLetters: 'Inspektor',
    individualDescription:
      'Als Inspektor ist %fname wahrscheinlich systematisch, besonders loyal und zuverlässig. %fname ist stolz auf die eigene Arbeit und fühlt sich bei Routineaufgaben wohl. %fname kann als ernsthaft und nachdenklich gelten, genießt es, Ordnung zu schaffen und durchzusetzen, ist wahrscheinlich präzise, systematisch, verantwortungsbewusst und arbeitet gern mit Fakten. %fname übernimmt Verantwortung, hält sich an Fristen und toleriert kaum Unentschlossenheit und Faulheit. Pflichtbewusstsein ist wichtig, und einmal engagiert, verfolgt %fname Projekte konsequent und trifft Entscheidungen basierend auf Fakten und Logik.',
  },
  ISTP: {
    shortDescription: 'Mechaniker',
    atWorkParagraphs: {
      effectiveness: [
        'Darauf achten, nicht zu viele Aufgaben gleichzeitig zu bearbeiten. Wichtige Aufgaben abschließen, bevor neue begonnen werden',
        'Weniger wählerisch bei Details sein',
        'Darauf achten, anderen nicht den Eindruck von Gleichgültigkeit zu vermitteln',
        'Energie darauf konzentrieren, eine Krise nach der anderen zu lösen',
        'Eine straffe Struktur schaffen, die Handlungsspielräume einschränkt',
        'Mehr Zeit für Vorbereitung einplanen',
        'Eine Agenda für Meetings vorbereiten',
        'Networking-Fähigkeiten trainieren',
        'Interpersonelle Bedürfnisse von Kollegen häufiger berücksichtigen',
        'Auf Details genauer achten',
        'Den Wert von Small Talk erkennen',
      ],
      teamLeader: [
        'Nimmt eine pragmatische Herangehensweise an Führung ein',
        'Führt das Team mit stiller, indirekter Autorität',
        'Bewertet alle Ansichten, mit der klaren Absicht, in die Umsetzung zu gehen',
        'Ermutigt das Team, durchzuhalten',
        'Geht sehr gut mit Krisen und Veränderungen um',
        'Weiß, wo die benötigten Informationen zu finden sind',
        'Löst Probleme logisch und auf Grundlage von Fakten',
      ],
      conflicts: [
        'Fällt es wahrscheinlich schwer, mit Teammitgliedern zu arbeiten, die sie als zu emotional statt objektiv wahrnehmen',
        'Kann Schwierigkeiten mit Menschen haben, die ihrer Meinung nach Energie für Aufgaben außerhalb des Teamziels verschwenden',
        'Kann irritiert sein von Teammitgliedern, die häufig Informationsmeetings abhalten, deren praktischer Zweck nicht klar ist',
      ],
      stressAdvisor: [
        'Kann bei Stress zu impulsiven, emotionalen Ausbrüchen neigen',
        'Fällt es möglicherweise schwer, Aufgaben konsequent abzuschließen',
        'Kümmert sich zu sehr um aktuelle Details und vernachlässigt zukünftige Möglichkeiten',
      ],
      workEnvironment: [
        'Sucht Flexibilität bei Routineaufgaben und benötigt wenig Aufsicht, da er gerne spontan handelt',
        'Genießt das Experimentieren mit Ideen und das Finden neuartiger Lösungen',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Eine Möglichkeit für %fname, die Arbeitseffektivität zu steigern, besteht darin, sich weniger auf Details zu fokussieren',
    withOthersTagLine: '---',
    withOthersSneakPeek: '%fname fühlt sich im Team am wohlsten in den Rollen: Der Kritiker, Der Präzise, Der Organisator',
    withOthersParagraphs: {
      strengths: [
        'Tolerant und aufgeschlossen',
        'Unerwartete Ausbrüche von originellem Humor',
        'Analysiert das Leben neugierig',
        'Genießt das Leben im Moment',
        'Handlungsorientiert und spontan',
        'Lebensfroh',
        'Tiefgründig',
        'Nachdenklich',
        'Reflektiert',
        'Selbstständig',
        'Bevorzugt möglicherweise schriftliche Kommunikation',
        'Fair und objektiv',
      ],
      weakSpotsWithOthers: [
        'Kann als zu impulsiv wahrgenommen werden',
        'Fühlt sich leicht eingeschränkt',
        'Zögert möglicherweise zunächst, bevor er Beiträge leistet',
        'Emotionalere Menschen haben Schwierigkeiten mit seinem sehr rationalen Ansatz',
        'Kann als zurückhaltend wahrgenommen werden',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: "%fname's Typ wird ISTP genannt. Einige Schlüsselwörter: objektiv, logisch, tolerant",
    typeKeywords: [
      '1. Bodenständig',
      '2. Logisch',
      '3. Analytisch',
      '4. Faktenorientiert',
      '5. Anpassungsfähig',
      '6. Pragmatistisch',
      '7. Tolerant',
      '8. Unabhängig',
      '9. Objektiv',
      '10. Fair',
      '11. Problemlöser',
    ],
    typePersonalStrengths: [
      'Gut darin, zu verstehen, wie Dinge funktionieren',
      'Gut darin, das Team in Aktion zu bringen',
      'Starker Fokus auf praktische Lösungen',
      'Natürlicher Troubleshooter: findet schnell praktikable Lösungen',
      'Gut darin, analytische Fähigkeiten zur effizienten Organisation einzusetzen',
      'Flexibel und anpassungsfähig',
      'Gut im Organisieren von Fakten und Anwenden logischer Prinzipien',
      'Pragmatische, realistische, praxisorientierte Herangehensweise',
      'Starkes logisches Denken',
      'Gut im Planen',
      'Bereit für Abenteuer und Herausforderungen',
      'Gut im Umgang mit Krisen',
      'Schätzt Logik und rationales Denken',
      'Bereit, zu handeln',
      'Reaktionsschnell und ausdauernd',
      'Gut darin, selbstständig zu arbeiten',
      'Bereit, Regeln zu brechen, wenn sie keinen Sinn ergeben',
      'Lösungsorientierter Realist',
      'Komfortabel im Umgang mit Fakten und Daten',
    ],
    typeWeakSpots: [
      'Nicht nur beobachten, sondern aktiv einbringen',
      'Weniger distanziert wirken',
      'Routine nicht vernachlässigen auf der Suche nach Abwechslung',
      'Mehr Interesse an abstrakten Theorien zeigen',
    ],
    description:
      'Der ISTP fühlt sich wahrscheinlich zu Rollen hingezogen, die Aktion erfordern. Er ist praxisorientiert, sehr enthusiastisch und energiegeladen. Der ISTP ist relativ entspannt, bevorzugt Flexibilität gegenüber Organisation und lässt seine Neugier ihn neue Wege entdecken. Logisch und praktisch veranlagt, mit Wissensdurst, fühlt er sich nicht besonders wohl bei Planung und Organisation und bevorzugt einen flexiblen Ansatz.',
    individualTypeByLetters: 'Mechaniker',
    individualDescription:
      'Als Mechaniker fühlt sich %fname wahrscheinlich zu Rollen hingezogen, die Aktion erfordern. %fname arbeitet gerne praktisch, ist sehr enthusiastisch und energiegeladen bei Projekten. %fname ist relativ entspannt, bevorzugt Flexibilität gegenüber Organisation und lässt seine Neugier neue Wege erkunden. %fname ist logisch und praktisch veranlagt, durstig nach Wissen, fühlt sich nicht besonders wohl bei Planung oder Organisation und bevorzugt einen flexiblen Ansatz.',
  },
};

export const tmiTypeGroupsDe: { name: string; types: string[] }[] = [
  {
    name: 'Handwerker',
    types: ['ISFP', 'ESTP', 'ESFP', 'ISTP'],
  },
  {
    name: 'Wächter',
    types: ['ESTJ', 'ISTJ', 'ESFJ', 'ISFJ'],
  },
  {
    name: 'Idealisten',
    types: ['ENFJ', 'INFJ', 'ENFP', 'INFP'],
  },
  {
    name: 'Denker',
    types: ['ENTJ', 'INTJ', 'ENTP', 'INTP'],
  },
];

export const spectrumTraitsDe: {
  [trait: string]: {
    imageUrl: string;
    left: {
      title: string;
      description: string;
    };
    right: {
      title: string;
      description: string;
    };
  };
} = {
  OPPORTUNITY_AWARENESS: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Chancenbewusst',
      description: 'Erkennt Möglichkeiten und Chancen',
    },
    right: {
      title: 'Kategorisch',
      description: 'Sieht Dinge als eindeutig und klar definiert',
    },
  },
  ACTION_ORIENTATION: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Handlungsorientiert',
      description: 'Eigeninitiativ, energisch, springt in Aktion',
    },
    right: {
      title: 'Zögerlich',
      description: 'Überlegt handeln, geduldig',
    },
  },
  DRIVE: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Annehmend',
      description: 'Regelmäßig, weniger anspruchsvoll',
    },
    right: {
      title: 'Leistungsorientiert',
      description: 'Ambitioniert, herausfordernd, hoher Druck',
    },
  },
  SELF_EFFICACY: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Besorgt',
      description: 'Kann herausfordernde Ziele als unerreichbar und nicht machbar wahrnehmen',
    },
    right: {
      title: 'Zuversichtlich',
      description: 'Glaubt an die eigene Kompetenz, neue Herausforderungen zu bewältigen',
    },
  },
  PERSISTENCE: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Sofortig',
      description: 'Sucht nach unmittelbaren, erreichbaren Erfolgen',
    },
    right: {
      title: 'Beharrlich',
      description: 'Verfolgt schwierige Aufgaben, kann das Hier und Jetzt vernachlässigen',
    },
  },
  ORDERLINESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Gewissenhaft',
      description: 'Gut organisiert, geplant, ordentlich',
    },
    right: {
      title: 'Flexibel',
      description: 'Ad hoc, zweckmäßig, pragmatisch',
    },
  },
  CAUTIOUSNESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Vorsichtig',
      description: 'Sicher, umsichtig, traditionell, routinemäßig',
    },
    right: {
      title: 'Impulsiv',
      description: 'Experimentierfreudig, herausfordernd, riskant, informell',
    },
  },
  DUTIFULNESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Pflichtbewusst',
      description: 'Erledigt Aufgaben sorgfältig, folgt Regeln',
    },
    right: {
      title: 'Anpassungsfähig',
      description: 'Betrachtet Pflichten flexibler und offen für Anpassungen',
    },
  },
  ALTRUISM: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Altruistisch',
      description: 'Freundlich, großzügig, Gemeinwohl orientiert',
    },
    right: {
      title: 'Gegenseitig',
      description: "Gleiches mit Gleichem vergelten, 'eine Gefälligkeit für eine Gefälligkeit', gerechter Austausch",
    },
  },
  COOPERATION: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Kooperativ',
      description: 'Harmonisch, kompromissbereit, entgegenkommend',
    },
    right: {
      title: 'Wettbewerbsorientiert',
      description: 'Stur, weniger kompromissbereit',
    },
  },
  ETHICS: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Offen',
      description: 'Natürlich, ehrlich, sieht keinen Grund zu täuschen',
    },
    right: {
      title: 'Zurückhaltend',
      description: 'Geformt, höflich, glaubt, dass ein gewisses Maß an Vorspiegelung notwendig ist',
    },
  },
  ASSERTIVENESS: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Entgegenkommend',
      description: 'Willensstark, macht ihre Positionen klar',
    },
    right: {
      title: 'Durchsetzungsfähig',
      description: 'Willensstark, macht ihre Positionen klar',
    },
  },
  EMOTIONALITY: {
    imageUrl: compassImageUrl,
    left: {
      title: 'Mitfühlend',
      description: 'Zeigt Verständnis für die Gefühle anderer, fürsorglich',
    },
    right: {
      title: 'Ruhig',
      description: 'Unbeeindruckt von Gefühlen, gleichgültig',
    },
  },
  GROUNDEDNESS: {
    imageUrl: compassImageUrl,
    left: {
      title: 'Praktisch',
      description: 'Greifbar, konkret, bodenständig, klar',
    },
    right: {
      title: 'Intellektuell neugierig',
      description: 'Konzeptuell, komplex, genießt Debatten, abstraktes Denken',
    },
  },
  IMAGINATION: {
    imageUrl: lightBulbImageUrl,
    left: {
      title: 'Eingeschränkt',
      description: 'Methodisch, automatisiert, weniger kreativ',
    },
    right: {
      title: 'Phantasievoll',
      description: 'Kreativ, verspielt, ideenorientiert',
    },
  },
  FRIENDLINESS: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Nüchtern',
      description: 'Ernst, könnte als weniger zugänglich wahrgenommen werden',
    },
    right: {
      title: 'Freundlich',
      description: 'Zugänglich, umgänglich',
    },
  },
  INDIVIDUALITY: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Gruppenorientiert',
      description: 'Fällt es leichter, sich anzupassen und Gruppenverhalten zu übernehmen',
    },
    right: {
      title: 'Individualistisch',
      description: 'Selbstständig, unabhängig, einzigartig',
    },
  },
  SOCIABILITY: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Privat',
      description: 'Genießt Zeit für sich selbst, zurückhaltend',
    },
    right: {
      title: 'Gesellig',
      description: 'Aufgeschlossen, gesprächig, genießt soziale Kontakte',
    },
  },
};
